import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  LinearProgress,
  Snackbar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  Assessment,
  Search,
  Description,
  AutoAwesome,
  Edit,
  Delete,
  Close,
  VideoLibrary,
  Article,
  ArrowBack,
  LockOpen,
  Download,
  AttachFile,
  Movie,
  Warning,
  CheckCircle,
} from '@mui/icons-material'
import { useAppSelector } from '../hooks/redux'
import { API_BASE_URL, buildApiUrl } from '../config/api'
import { useGetCasesQuery } from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import { useGetMemorialsQuery, useLazyDownloadMemorialQuery, useLazyDownloadMemorialAsPdfQuery } from '../store/api/memorialsApi'
import { useGetDocumentsQuery, useLazyDownloadDocumentQuery, useSearchDocumentsScoringMutation } from '../store/api/documentsApi'
import { useGetEvidenceLocksForCaseQuery, useGetEvidenceQuery } from '../store/api/evidenceApi'
import { useGetR1CaseSummaryQuery, INDEX_TO_SIDE, type R1Side } from '../store/api/r1SubmissionsApi'
import {
  useGetScoresQuery,
  useCreateScoreMutation,
  useUpdateScoreMutation,
  useDeleteScoreMutation,
  useSubmitScoreMutation,
  useUnlockScoreMutation,
  useReopenScoreMutation,
  useLazyGetAISuggestionsQuery,
  useGetRubricsQuery,
  useCreateRubricMutation,
  useRunAiPreliminaryMemorialMutation,
  useRunAiEvidenceClassificationMutation,
  useGetScorecardProgressQuery,
  useProxyEnterScoreMutation,
  type RubricCreate,
  type RubricCriterion,
  type Rubric,
  type Score,
} from '../store/api/scoringApi'
import { useGetUsersQuery } from '../store/api/usersApi'
import { useLazyExportScoresCsvQuery } from '../store/api/reportsApi'
import { hasPermission } from '../utils/permissions'
import { gradientBrand } from '../theme/brand'
import { formatCaseDisplay, getClaimantName } from '../utils/caseDisplay'
import { PageHeader } from '../components/PageHeader'
import { getTablePaperSx } from '../theme/sectionSurfaces'

/**
 * Spec G3 (audit 2026-05-21) — Bảng admin chấm điểm cho case dual_claimant_packets.
 *
 * Mỗi đội 1 row, 2 cột HS1 + HS2 với cùng cấu trúc:
 *   - Tên nguyên đơn (lấy từ case.scenario_a/b_claimant_name)
 *   - Trạng thái Đơn khởi kiện (chưa nộp / đã nộp / đã khóa)
 *   - Trạng thái Chứng cứ (count + locked badge)
 *   - Điểm HS (avg total_score qua judges đã chấm — admin chấm = judge_id = admin.id)
 *   - Nút Chấm điểm — mở dialog với submission_index pre-set
 *
 * Lấy dữ liệu từ /r1-submissions/case-summary (đã sync lock state) + scores +
 * memorials (để check is_official_submission) + evidence (đếm theo r1_side).
 */
interface R1DualPanelTeam {
  id: string
  name: string
  university?: string | null
}
interface R1DualPanelProps {
  caseItem: {
    id: string
    title: string
    plaintiff?: string | null
    defendant?: string | null
    scenario_a_claimant_name?: string | null
    scenario_b_claimant_name?: string | null
    assigned_team_ids?: string[] | null
  }
  teams: R1DualPanelTeam[]
  memorials: Array<any>
  evidence: Array<any>
  scores: Array<any>
  // Bug fix 2026-05-22 — pass `side` (scenario_a/b) để dialog filter chứng cứ +
  // gán submission_index khi tạo phiếu mới (1 = scenario_a, 2 = scenario_b).
  onOpenScoring: (
    caseId: string,
    teamId: string,
    submissionId?: string,
    submissionType?: 'memorial' | 'transcript' | 'evidence' | 'intro_video',
    side?: R1Side,
  ) => void
  selectedTeamId: string
  // 2026-05-25: judge KHÔNG chấm R1 (Sơ loại) — chỉ admin/secretary chấm
  // trực tiếp. Judge readOnly → ẩn 2 button "Chấm Đơn" + "Chấm CC".
  readOnly?: boolean
}
function R1DualTeamScoringPanel({
  caseItem,
  teams,
  memorials,
  evidence,
  scores,
  onOpenScoring,
  selectedTeamId,
  readOnly = false,
}: R1DualPanelProps) {
  const { data: summary = [] } = useGetR1CaseSummaryQuery({ case_id: caseItem.id })
  const assignedTeams = useMemo<R1DualPanelTeam[]>(() => {
    const ids = caseItem.assigned_team_ids || []
    return ids
      .map((tid: string) => teams.find((t) => t.id === tid))
      .filter((t): t is R1DualPanelTeam => !!t)
  }, [caseItem.assigned_team_ids, teams])

  const renderCard = (
    teamId: string,
    side: R1Side,
  ) => {
    const submissionIndex: 1 | 2 = side === 'scenario_a' ? 1 : 2
    const themeBg = side === 'scenario_a' ? '#fce4ec' : '#e3f2fd'
    const themeFg = side === 'scenario_a' ? '#c2185b' : '#1565c0'
    const claimant = getClaimantName(caseItem as any, side)
    const teamSummary = summary.find((s) => s.team_id === teamId)
    const packet = side === 'scenario_a' ? teamSummary?.scenario_a : teamSummary?.scenario_b

    // Memorial: bản chính thức theo r1_side
    const officialMemorial = memorials.find(
      (m) =>
        m.case_id === caseItem.id &&
        m.team_id === teamId &&
        (m as any).r1_side === side &&
        m.is_official_submission,
    )
    // Evidence: count theo r1_side
    const evidenceItems = evidence.filter(
      (e) => e.case_id === caseItem.id && e.team_id === teamId && (e as any).r1_side === side,
    )
    const evidenceLocked = !!packet?.evidence_locked

    // Điểm: scores có submission_index khớp, lấy AVG total_score.
    // 2026-05-26 (UAT triệt để): legacy scores trước Phase 4.1 có
    // submission_index=null → cả 2 cards HS1/HS2 đều không match → admin
    // tưởng "2 hồ sơ chấm bằng điểm" vì cả 2 đều show 0.00. Hoặc tệ hơn,
    // nếu UPDATE legacy score qua dialog mới với side=scenario_a/b, cả 2
    // dialog có thể đọc cùng score record → admin chấm "chung".
    // Fix: STRICT match submission_index trước; fallback memorial_id cho
    // memorial (legacy data). Đếm duplicate → cảnh báo admin.
    const teamScoresAll = scores.filter(
      (s) => s.case_id === caseItem.id && s.team_id === teamId,
    )
    const memorialScore =
      teamScoresAll.find(
        (s) =>
          s.submission_type === 'memorial' &&
          (s as any).submission_index === submissionIndex,
      ) ||
      (officialMemorial?.id
        ? teamScoresAll.find(
            (s) =>
              s.submission_type === 'memorial' &&
              ((s as any).submission_index == null) &&
              s.memorial_id === officialMemorial.id,
          )
        : undefined)
    const evidenceScore = teamScoresAll.find(
      (s) =>
        s.submission_type === 'evidence' &&
        (s as any).submission_index === submissionIndex,
    )
    const totalScore =
      (memorialScore?.total_score || 0) + (evidenceScore?.total_score || 0)
    const hasAnyScore = !!memorialScore || !!evidenceScore
    // Cảnh báo: duplicate score cùng (team, type, index) → có thể admin
    // chấm trùng hoặc legacy data. Hiển thị warning cho admin biết dọn.
    const memorialDuplicates = teamScoresAll.filter(
      (s) =>
        s.submission_type === 'memorial' &&
        (s as any).submission_index === submissionIndex,
    ).length
    const evidenceDuplicates = teamScoresAll.filter(
      (s) =>
        s.submission_type === 'evidence' &&
        (s as any).submission_index === submissionIndex,
    ).length
    const hasDuplicates = memorialDuplicates > 1 || evidenceDuplicates > 1
    // Đếm legacy null-index scores → cảnh báo dọn dữ liệu.
    const legacyNullIndexCount = teamScoresAll.filter(
      (s) =>
        (s.submission_type === 'memorial' || s.submission_type === 'evidence') &&
        (s as any).submission_index == null,
    ).length

    return (
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderLeft: `4px solid ${themeFg}`,
          backgroundColor: alpha(themeBg, 0.4),
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={`Hồ sơ ${submissionIndex}`}
            size="small"
            sx={{ bgcolor: themeBg, color: themeFg, fontWeight: 700 }}
          />
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
            Nguyên đơn: {claimant}
          </Typography>
        </Box>
        <Divider />

        {/* Đơn khởi kiện */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ minWidth: 80, color: 'text.secondary' }}>
            Đơn KK:
          </Typography>
          {officialMemorial ? (
            <Chip
              size="small"
              label={packet?.memorial_locked ? 'Đã khóa' : 'Đã nộp'}
              color={packet?.memorial_locked ? 'primary' : 'success'}
              variant={packet?.memorial_locked ? 'filled' : 'outlined'}
            />
          ) : (
            <Chip size="small" label="Chưa nộp" color="warning" variant="outlined" />
          )}
        </Box>

        {/* Chứng cứ */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ minWidth: 80, color: 'text.secondary' }}>
            Chứng cứ:
          </Typography>
          <Chip
            size="small"
            label={`${evidenceItems.length} mục${evidenceLocked ? ' ✓' : ''}`}
            color={evidenceLocked ? 'success' : evidenceItems.length > 0 ? 'default' : 'warning'}
            variant={evidenceLocked ? 'filled' : 'outlined'}
          />
        </Box>

        {/* Điểm */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="caption" sx={{ minWidth: 80, color: 'text.secondary' }}>
            Điểm:
          </Typography>
          {hasAnyScore ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" fontWeight={700} color="primary.main">
                {totalScore.toFixed(1)} / 100
              </Typography>
              <Typography variant="caption" color="text.secondary">
                (Đơn {memorialScore?.total_score?.toFixed(1) || '0'} + CC {evidenceScore?.total_score?.toFixed(1) || '0'})
              </Typography>
              {memorialScore?.id && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ fontFamily: 'monospace', ml: 0.5, fontSize: 10 }}
                  title={`Memorial score_id: ${memorialScore.id}`}
                >
                  #{memorialScore.id.slice(0, 6)}
                </Typography>
              )}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" fontStyle="italic">
              Chưa chấm
            </Typography>
          )}
        </Box>
        {(hasDuplicates || legacyNullIndexCount > 0) && (
          <Alert severity="warning" sx={{ py: 0, my: 0.5, '& .MuiAlert-message': { py: 0.5 } }}>
            <Typography variant="caption" fontWeight={600}>
              {hasDuplicates && `Có ${memorialDuplicates + evidenceDuplicates} phiếu duplicate cùng (đội, loại, hồ sơ). `}
              {legacyNullIndexCount > 0 && `${legacyNullIndexCount} phiếu legacy thiếu submission_index. `}
              Liên hệ BTC để dọn — có thể gây "chấm chung 2 hồ sơ".
            </Typography>
          </Alert>
        )}

        {/* Nút chấm */}
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 1 }}>
          {/* 2026-05-25: ẩn nút "Chấm Đơn" + "Chấm CC" cho judge — judge KHÔNG
              chấm R1 (Sơ loại) theo spec BTC, chỉ admin/secretary chấm trực
              tiếp. Judge có thể VIEW score qua main list (chip điểm bên dưới). */}
          {!readOnly && (
            <>
              <Button
                size="small"
                variant={memorialScore ? 'outlined' : 'contained'}
                color="primary"
                startIcon={<Description />}
                disabled={!officialMemorial}
                onClick={() =>
                  onOpenScoring(caseItem.id, teamId, officialMemorial?.id, 'memorial', side)
                }
                sx={{ textTransform: 'none' }}
              >
                {memorialScore ? 'Sửa Đơn' : 'Chấm Đơn'}
              </Button>
              <Button
                size="small"
                variant={evidenceScore ? 'outlined' : 'contained'}
                color="secondary"
                startIcon={<AttachFile />}
                disabled={evidenceItems.length === 0}
                onClick={() => onOpenScoring(caseItem.id, teamId, undefined, 'evidence', side)}
                sx={{ textTransform: 'none' }}
              >
                {evidenceScore ? 'Sửa CC' : 'Chấm CC'}
              </Button>
            </>
          )}
          {readOnly && (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              Giám khảo chỉ xem điểm Vòng Sơ loại — KHÔNG chấm trực tiếp.
            </Typography>
          )}
        </Box>
      </Paper>
    )
  }

  const visibleTeams = selectedTeamId
    ? assignedTeams.filter((t) => t.id === selectedTeamId)
    : assignedTeams

  if (visibleTeams.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        Cuộc thi chưa được phân đội.
      </Alert>
    )
  }

  return (
    <Paper sx={{ p: 2, mb: 3 }} elevation={0}>
      <Typography variant="h6" gutterBottom>
        Chấm điểm 2 hồ sơ — Vòng Sơ loại
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Mỗi đội nộp 2 hồ sơ (HS1 — vai trò Nguyên đơn A, HS2 — vai trò Nguyên đơn B).
        Tổng điểm chính thức = HS1 + HS2 (max 200).
      </Typography>
      <Stack spacing={2}>
        {visibleTeams.map((team) => (
          <Box key={team.id}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
              {team.name}{team.university ? ` — ${team.university}` : ''}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                {renderCard(team.id, 'scenario_a')}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderCard(team.id, 'scenario_b')}
              </Grid>
            </Grid>
          </Box>
        ))}
      </Stack>
    </Paper>
  )
}

export default function ScoringPage() {
  const { user: currentUser, token } = useAppSelector((state) => state.auth)
  const [scoringDialogOpen, setScoringDialogOpen] = useState(false)
  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  const [selectedTeamId, setSelectedTeamId] = useState<string>('')
  const [viewMode, setViewMode] = useState<'caseList' | 'caseDetail'>('caseList')
  // YC16 (audit 2026-05-23): bộ lọc cho "Danh sách cần chấm điểm".
  // - filterSubType: lọc theo loại submission (memorial/evidence/transcript/intro_video).
  // - filterScoreStatus: 'all' | 'unscored' (chưa chấm) | 'scored' (đã có phiếu).
  // - sortByScore: '' | 'asc' | 'desc' — sắp xếp theo điểm hiện có.
  const [filterSubType, setFilterSubType] = useState<'' | 'memorial' | 'evidence' | 'transcript' | 'intro_video'>('')
  const [filterScoreStatus, setFilterScoreStatus] = useState<'all' | 'unscored' | 'scored'>('all')
  const [sortByScore, setSortByScore] = useState<'' | 'asc' | 'desc'>('')
  const [ragResults, setRagResults] = useState<any[]>([])
  const [isRAGSearching, setIsRAGSearching] = useState(false)
  const [editingScore, setEditingScore] = useState<Score | null>(null)
  const [aiSuggestions, setAISuggestions] = useState<any>(null)
  const [isLoadingAI, setIsLoadingAI] = useState(false)
  const [aiElapsedSecs, setAiElapsedSecs] = useState(0)
  const [rubricDialogOpen, setRubricDialogOpen] = useState(false)
  const [newRubric, setNewRubric] = useState<RubricCreate>({
    name: '',
    case_id: '',
    criteria: [
      { key: 'criterion_1', name: 'Tiêu chí 1', max_score: 10, weight: 1 }
    ],
  })

  const { data: cases = [] } = useGetCasesQuery()
  const { data: teams = [], isLoading: isLoadingTeams } = useGetTeamsQuery()
  const { data: memorials = [] } = useGetMemorialsQuery(
    { case_id: selectedCaseId || undefined },
    { skip: false } // Load all memorials, filter will be done in UI
  )
  // Load transcripts (documents with type TRANSCRIPT)
  const { data: transcripts = [] } = useGetDocumentsQuery(
    { 
      case_id: selectedCaseId || undefined,
      document_type: 'transcript'
    },
    { skip: false }
  )
  const { data: scores = [], refetch: refetchScores } = useGetScoresQuery(
    { case_id: selectedCaseId || undefined, team_id: selectedTeamId || undefined },
    { skip: false } // Load all scores, filter will be done in UI
  )
  // Load all rubrics (both general and case-specific)
  const { data: rubrics = [] } = useGetRubricsQuery(
    {}, // Load all rubrics, not filtered by case_id
    { skip: false }
  )
  // Load evidence locks (đội đã nộp chính thức) — phục vụ row chấm evidence
  const { data: evidenceLocks = [] } = useGetEvidenceLocksForCaseQuery(
    selectedCaseId,
    { skip: !selectedCaseId },
  )

  // G3 (audit 2026-05-21): toàn bộ evidence của case (không filter team) — phục
  // vụ bảng dual scoring panel để đếm CC mỗi (team, r1_side).
  const { data: caseEvidenceList = [] } = useGetEvidenceQuery(
    { case_id: selectedCaseId },
    { skip: !selectedCaseId },
  )

  const [createScore, { isLoading: isCreating }] = useCreateScoreMutation()
  const [updateScore, { isLoading: isUpdating }] = useUpdateScoreMutation()
  const [deleteScore] = useDeleteScoreMutation()
  const [submitScore, { isLoading: isSubmitting }] = useSubmitScoreMutation()
  const [unlockScore, { isLoading: isUnlocking }] = useUnlockScoreMutation()
  const [reopenScoreM, { isLoading: isReopening }] = useReopenScoreMutation()
  const [proxyEnterScore, { isLoading: isProxySubmitting }] = useProxyEnterScoreMutation()

  // 2026-05-25: Dialog mở khoá phiếu Bán kết / Chung kết — yêu cầu lý do
  // bắt buộc + audit log (BE auto-ghi qua AuditLogService.log_action với
  // action=score.unlock hoặc score.reopen). Hiển thị unlock_history để admin
  // thấy ai đã unlock trước đó. Trước đây dùng window.prompt() — không
  // capture được context (team/judge), không cho chọn unlock vs reopen.
  const [unlockDialog, setUnlockDialog] = useState<{
    open: boolean
    score: Score | null
    mode: 'unlock' | 'reopen'
    reason: string
    submitting: boolean
    errorMsg: string
  }>({
    open: false,
    score: null,
    mode: 'unlock',
    reason: '',
    submitting: false,
    errorMsg: '',
  })

  // Phase 5 publish-lock notice (2026-05-25): cảnh báo trực quan khi case đã có
  // ít nhất 1 round được công bố (team có competition_status hoặc final_rank).
  // BE đã guard cứng — banner này chỉ giảm friction để judge biết trước rằng
  // tạo/sửa phiếu chấm cho vòng đã chốt sẽ bị reject.
  const publishedNotice = useMemo<{ hasPublished: boolean; teamPublishedStage: string | null }>(() => {
    if (!selectedCaseId) return { hasPublished: false, teamPublishedStage: null }
    const caseTeams = teams.filter((t) => (t.assigned_cases || []).includes(selectedCaseId))
    const hasPublished = caseTeams.some(
      (t) => !!t.competition_status || (t.final_rank != null && t.final_rank > 0),
    )
    let teamPublishedStage: string | null = null
    if (selectedTeamId) {
      const selT = caseTeams.find((t) => t.id === selectedTeamId)
      const cs = selT?.competition_status || null
      if (cs === 'advanced_to_semifinal') teamPublishedStage = 'Vòng Sơ loại đã công bố — đội này đã vào Bán kết. Phiếu chấm Sơ loại đã khoá.'
      else if (cs === 'advanced_to_final') teamPublishedStage = 'Vòng Bán kết đã công bố — đội này đã vào Chung kết. Phiếu chấm Sơ loại + Bán kết đã khoá.'
      else if (cs === 'eliminated') teamPublishedStage = 'Đội này đã bị loại — vòng tương ứng đã công bố. Phiếu chấm các vòng cũ đã khoá.'
    }
    return { hasPublished, teamPublishedStage }
  }, [teams, selectedCaseId, selectedTeamId])

  // Admin view: theo dõi tiến độ chấm Bán kết/Chung kết real-time
  const isAdminScoring = !!currentUser && hasPermission(currentUser.role, 'MANAGE_USERS')
  const { data: scorecardProgress = [], refetch: refetchProgress } = useGetScorecardProgressQuery(
    { case_id: selectedCaseId || undefined },
    { skip: !selectedCaseId || !isAdminScoring, pollingInterval: 30000 },
  )
  const { data: judgeUsers = [] } = useGetUsersQuery(
    { role: 'judge' as any },
    { skip: !isAdminScoring },
  )

  // Proxy entry dialog state
  const [proxyDialog, setProxyDialog] = useState<{
    open: boolean
    scoreId: string | null
    judgeId: string
    teamId: string
    rubricId: string | null
    rubricScores: Record<string, number>
    comments: string
    proxyReason: 'no_device' | 'tech_issue' | 'requested' | 'other'
    delegatingJudgeId: string
    submit: boolean
  }>({
    open: false,
    scoreId: null,
    judgeId: '',
    teamId: '',
    rubricId: null,
    rubricScores: {},
    comments: '',
    proxyReason: 'no_device',
    delegatingJudgeId: '',
    submit: true,
  })

  const openProxyDialog = (score: Score) => {
    setProxyDialog({
      open: true,
      scoreId: score.id,
      judgeId: score.judge_id,
      teamId: score.team_id,
      rubricId: score.rubric_id || null,
      rubricScores: { ...(score.rubric_scores || {}) },
      comments: score.comments || '',
      proxyReason: 'no_device',
      delegatingJudgeId: score.judge_id,
      submit: true,
    })
  }
  const closeProxyDialog = () => setProxyDialog((s) => ({ ...s, open: false }))

  const handleProxySubmit = async () => {
    if (!proxyDialog.scoreId) return
    try {
      await proxyEnterScore({
        id: proxyDialog.scoreId,
        rubric_scores: proxyDialog.rubricScores,
        comments: proxyDialog.comments || undefined,
        proxy_reason: proxyDialog.proxyReason,
        delegating_judge_id: proxyDialog.delegatingJudgeId || undefined,
        submit: proxyDialog.submit,
      }).unwrap()
      setSnackbar({
        open: true,
        message: proxyDialog.submit
          ? 'Đã nhập thay + chốt phiếu.'
          : 'Đã lưu nháp phiếu (chưa chốt).',
        severity: 'success',
      })
      closeProxyDialog()
      refetchScores()
      refetchProgress()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Nhập thay thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }
  const [getAISuggestions] = useLazyGetAISuggestionsQuery()
  const [createRubric, { isLoading: isCreatingRubric }] = useCreateRubricMutation()
  const [downloadDocument] = useLazyDownloadDocumentQuery()
  const [searchDocumentsScoring] = useSearchDocumentsScoringMutation()
  const [downloadMemorial] = useLazyDownloadMemorialQuery()
  const [downloadMemorialAsPdf] = useLazyDownloadMemorialAsPdfQuery()

  // PDF preview state cho Memorial scoring (embed iframe trong dialog)
  const [memorialPreviewUrl, setMemorialPreviewUrl] = useState<string | null>(null)
  const [memorialPreviewLoading, setMemorialPreviewLoading] = useState(false)
  const [memorialPreviewError, setMemorialPreviewError] = useState<string | null>(null)
  const [exportScoresCsv] = useLazyExportScoresCsvQuery()
  const [runAiPreliminary, { isLoading: prelimLoading }] = useRunAiPreliminaryMemorialMutation()
  const [runAiEvidence, { isLoading: aiEvidenceLoading }] = useRunAiEvidenceClassificationMutation()
  const [aiEvidenceData, setAiEvidenceData] = useState<any>(null)

  const handleLoadAiEvidence = async () => {
    if (!scoringForm.case_id || !scoringForm.team_id) {
      alert('Cần có case + team trong phiếu chấm')
      return
    }
    // 2026-05-26: truyền submission_index (1=HS1, 2=HS2) → BE filter
    // evidence theo r1_side → AI tính riêng cho từng hồ sơ. Không truyền
    // → BE gom toàn bộ → HS1 và HS2 ra cùng kết quả (bug cũ).
    const subIdx = scoringForm.submission_index
    try {
      const data = await runAiEvidence({
        case_id: scoringForm.case_id,
        team_id: scoringForm.team_id,
        submission_index: (subIdx === 1 || subIdx === 2) ? subIdx : undefined,
      }).unwrap()
      setAiEvidenceData(data)
    } catch (e: any) {
      alert(`AI Chấm Chứng cứ thất bại: ${e?.data?.detail || e?.message || ''}`)
    }
  }

  // BRD §5 AI Governance: AI là báo cáo tham khảo, KHÔNG tự điền vào phiếu chấm.
  // Giám khảo phải đọc báo cáo + Đơn khởi kiện + Bản phụ lục chứng cứ rồi tự ghi điểm
  // theo 4 tiêu chí Rubric chuẩn. Hàm handleApplyAiEvidence cũ đã được loại bỏ.
  const [aiPreliminary, setAiPreliminary] = useState<{
    governance_notice?: string
    excerpts?: Array<Record<string, unknown>>
  } | null>(null)

  const canScoreCases = currentUser && hasPermission(currentUser.role, 'SCORE_CASES')
  const canUseAiAssistive =
    currentUser &&
    (hasPermission(currentUser.role, 'SCORE_CASES') || hasPermission(currentUser.role, 'VIEW_AI_REPORTS'))

  // State for transcript content
  const [transcriptContent, setTranscriptContent] = useState<string>('')
  const [isLoadingTranscript, setIsLoadingTranscript] = useState(false)

  // Intro-video blob URL — stream endpoint cần Authorization header (FileResponse),
  // không thể gán trực tiếp vào <video src>. Fetch blob → ObjectURL → revoke.
  const [introVideoBlobUrl, setIntroVideoBlobUrl] = useState<string | null>(null)
  const [isLoadingIntroVideo, setIsLoadingIntroVideo] = useState(false)
  const [introVideoError, setIntroVideoError] = useState<string | null>(null)

  // Handler for downloading transcript
  const handleDownloadTranscript = async (transcriptId: string, fileName: string) => {
    try {
      const result = await downloadDocument(transcriptId).unwrap()
      const url = window.URL.createObjectURL(result)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error: any) {
      alert(`Download transcript thất bại: ${error.data?.detail || error.message}`)
    }
  }

  // Handler for loading transcript content
  const handleLoadTranscriptContent = async (transcriptId: string) => {
    setIsLoadingTranscript(true)
    try {
      const result = await downloadDocument(transcriptId).unwrap()
      // Try to read as text
      const text = await result.text()
      setTranscriptContent(text)
    } catch (error: any) {
      console.error('Failed to load transcript content:', error)
      setTranscriptContent('')
    } finally {
      setIsLoadingTranscript(false)
    }
  }

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error' | 'warning' | 'info'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  // Scoring form state
  const [scoringForm, setScoringForm] = useState({
    case_id: '',
    team_id: '',
    memorial_id: '',
    transcript_id: '',
    submission_type: '' as 'memorial' | 'transcript' | 'evidence' | 'intro_video' | '',
    rubric_id: '',
    rubric_scores: {} as Record<string, number>,
    comments: '',
    // Spec PHẦN 4.1 — R1 dual_claimant_packets: submission_index 1 hoặc 2.
    // Null cho R2/R3 hoặc legacy single_role.
    submission_index: null as 1 | 2 | null,
    representing_role: '' as 'plaintiff' | 'defendant' | '',
  })

  // Danh sách chứng cứ đội đã nộp — hiển thị bên trái khi chấm phiếu Chứng cứ.
  // Bug fix 2026-05-22 — filter theo r1_side dựa vào scoringForm.submission_index:
  //   1 → scenario_a (HS1), 2 → scenario_b (HS2). Trước đây load gộp cả 2 hồ sơ
  //   → giám khảo nhìn lẫn lộn chứng cứ.
  // Truyền r1_side qua API để backend filter sẵn (giảm payload + tránh leak chứng
  // cứ giữa các hồ sơ). Client vẫn filter lần nữa cho an toàn (defense in depth).
  const evidenceSideParam: 'scenario_a' | 'scenario_b' | undefined =
    scoringForm.submission_index === 1
      ? 'scenario_a'
      : scoringForm.submission_index === 2
        ? 'scenario_b'
        : undefined
  const { data: teamEvidenceListRaw = [] } = useGetEvidenceQuery(
    { case_id: selectedCaseId, team_id: selectedTeamId, r1_side: evidenceSideParam },
    { skip: !selectedCaseId || !selectedTeamId },
  )
  const teamEvidenceList = useMemo(() => {
    const idx = scoringForm.submission_index
    if (idx !== 1 && idx !== 2) return teamEvidenceListRaw as any[]
    const side: 'scenario_a' | 'scenario_b' = idx === 1 ? 'scenario_a' : 'scenario_b'
    return (teamEvidenceListRaw as any[]).filter((e) => {
      const evSide = (e as any)?.r1_side
      // Strict mode: chỉ giữ evidence có r1_side khớp.
      // Backward compat: evidence cũ KHÔNG có r1_side (legacy single role / dữ liệu
      // di cư) → giữ lại để admin không bị mất view. Khi data đầy đủ r1_side thì
      // filter sẽ chính xác.
      if (!evSide) return true
      return evSide === side
    })
  }, [teamEvidenceListRaw, scoringForm.submission_index])

  const [ragQuery, setRagQuery] = useState('')

  // Rubric Video Sơ loại built-in (Điều 10.3) — sync với BE
  // ScoringService.get_intro_video_rubric. ID đặc biệt để FE/BE không lưu DB.
  const INTRO_VIDEO_RUBRIC_ID = '__builtin__intro_video__'
  const INTRO_VIDEO_RUBRIC: Rubric = {
    id: INTRO_VIDEO_RUBRIC_ID,
    name: 'Phiếu Video giới thiệu — Vòng Sơ loại (Điều 10.3)',
    description:
      'Tổng 30 điểm. Dùng làm tiêu chí phụ tie-break khi điểm bài thi chính bằng nhau.',
    criteria: [
      {
        key: 'creativity',
        name: 'Tính sáng tạo',
        description: 'Cách thể hiện, ý tưởng, dấu ấn riêng.',
        max_score: 10,
        weight: 1,
      },
      {
        key: 'presentation',
        name: 'Kỹ năng trình bày',
        description: 'Diễn đạt, ngôn ngữ, hình ảnh, phương tiện truyền thông.',
        max_score: 10,
        weight: 1,
      },
      {
        key: 'professionalism',
        name: 'Tính chuyên nghiệp',
        description: 'Hình thức, cấu trúc, đầu tư chất lượng video.',
        max_score: 10,
        weight: 1,
      },
    ],
  }

  // 2026-05-25: Rubric chính thức Vòng Bán kết — sync với BE
  // ScoringService.get_semifinal_rubric. 5 tiêu chí cố định, tổng 100đ.
  // Quy tắc: GK nhập trực tiếp, KHÔNG weight, KHÔNG AI suggest.
  const SEMIFINAL_RUBRIC_ID = '__builtin__semifinal__'
  const FINAL_RUBRIC_ID = '__builtin__final__'
  // 2026-05-26: Rubric R1 built-in — 8 tiêu chí (4 Đơn KK m_* max 50 + 4
  // Chứng cứ evidence_* max 50). FE filter qua prefix `evidence_` ở
  // rubricCriteria — form "Chấm Đơn" chỉ thấy m_*, form "Chấm CC" chỉ
  // thấy evidence_*. Mirror BE ScoringService.get_r1_rubric.
  const R1_RUBRIC_ID = '__builtin__r1__'
  const R1_CRITERIA = [
    {
      key: 'm_legal_analysis',
      name: 'A. Phân tích pháp lý',
      description:
        'Đánh giá khả năng phân tích vấn đề pháp lý, xác định đúng quan hệ pháp luật và quy phạm áp dụng.',
      max_score: 18,
      weight: 1,
    },
    {
      key: 'm_argumentation',
      name: 'B. Lập luận pháp lý',
      description:
        'Logic, mạch lạc, có căn cứ; kết nối điều luật → tình tiết → kết luận.',
      max_score: 14,
      weight: 1,
    },
    {
      key: 'm_presentation',
      name: 'C. Trình bày & cấu trúc',
      description:
        'Bố cục Đơn khởi kiện rõ ràng, ngôn ngữ pháp lý chuẩn mực, hình thức đúng quy định.',
      max_score: 10,
      weight: 1,
    },
    {
      key: 'm_citation',
      name: 'D. Trích dẫn & viện dẫn',
      description:
        'Trích đúng điều luật, án lệ, tài liệu tham khảo; chú thích đầy đủ; không sai/lệch nguồn.',
      max_score: 8,
      weight: 1,
    },
    // 2026-05-26 (fix): Phần Chứng cứ chấm AI auto-score theo "chứng cứ đúng
    // vs chứng cứ bẫy" — KHÔNG admin nhập tay. Match BE compute_evidence_auto_score:
    //  2.1 Thu thập (35đ): (picked_correct/total_correct) × 35
    //  2.2 Sàng lọc (15đ): max(0, 15 − 2 × picked_decoy)
    // 2 key cố định để aiEvidenceData payload (criterion_evidence_collection +
    // criterion_evidence_filtering) match được BE validation.
    {
      key: 'evidence_collection',
      name: '2.1. Thu thập chứng cứ hợp lệ',
      description:
        'Tính tự động: (picked_correct / total_correct) × 35. Càng chọn nhiều chứng cứ đúng → điểm càng cao.',
      max_score: 35,
      weight: 1,
    },
    {
      key: 'evidence_filtering',
      name: '2.2. Sàng lọc & loại trừ bẫy nhiễu',
      description:
        'Tính tự động: max(0, 15 − 2 × picked_decoy). Mỗi chứng cứ bẫy bị chọn nhầm → trừ 2đ.',
      max_score: 15,
      weight: 1,
    },
  ]
  const R1_RUBRIC: Rubric = {
    id: R1_RUBRIC_ID,
    name: 'Phiếu chấm Vòng Sơ loại — Đơn KK + Chứng cứ',
    description:
      '8 tiêu chí: 4 chấm Đơn KK (max 50đ, không có prefix) + 4 chấm Chứng cứ (max 50đ, prefix evidence_).',
    criteria: R1_CRITERIA,
  }
  const SEMIFINAL_CRITERIA = [
    {
      key: 'I_evidence_present',
      name: 'I. Kỹ năng xuất trình và bảo vệ chứng cứ',
      description:
        'Tư duy "lọc" và sử dụng chứng cứ (Điều 93, 94 BLTTDS). Lập luận tính khách quan/hợp pháp/liên quan; loại bỏ chứng cứ nhiễu; giải trình nguồn gốc khi Thẩm phán yêu cầu.',
      max_score: 20,
      weight: 1,
    },
    {
      key: 'II_evidence_rebuttal',
      name: 'II. Kỹ năng tiếp cận và phản bác chứng cứ đối phương',
      description:
        'Tư duy phản biện, ứng biến. Phát hiện điểm yếu chứng cứ đối phương; câu hỏi chất vấn sắc bén làm suy yếu giá trị chứng minh.',
      max_score: 20,
      weight: 1,
    },
    {
      key: 'III_negotiation',
      name: 'III. Kỹ năng đàm phán và tham gia hòa giải',
      description:
        'Nghệ thuật thương lượng Win-Win (Điều 205 BLTTDS). Phương án hòa giải khả thi, nhượng bộ nhưng giữ cốt lõi; dùng đòn bẩy pháp lý; thiện chí hỗ trợ Thẩm phán.',
      max_score: 25,
      weight: 1,
    },
    {
      key: 'IV_law_application',
      name: 'IV. Kiến thức pháp luật và áp dụng luật',
      description:
        'Viện dẫn chính xác Luật HN&GĐ 2014 (Điều 56, 59, 81, 82) và BLTTDS. Tính toán chia ~30 tỷ và bù trừ nghĩa vụ chính xác tại bàn hòa giải.',
      max_score: 20,
      weight: 1,
    },
    {
      key: 'V_conduct',
      name: 'V. Tác phong, ngôn ngữ và đạo đức nghề nghiệp',
      description:
        'Ngôn ngữ chuẩn mực, tôn trọng Thẩm phán/đồng nghiệp; không ngắt lời, kiểm soát cảm xúc; phân bổ thời gian hợp lý giữa 2 Luật sư cùng đội.',
      max_score: 15,
      weight: 1,
    },
  ]
  const SEMIFINAL_RUBRIC: Rubric = {
    id: SEMIFINAL_RUBRIC_ID,
    name: 'Phiếu chấm Vòng Bán kết — Phiên kiểm tra chứng cứ & hòa giải',
    description:
      'Tổng 100 điểm, 5 tiêu chí. GK nhập trực tiếp, không weight, không AI gợi ý.',
    criteria: SEMIFINAL_CRITERIA,
  }
  // 2026-05-26: Rubric Vòng Chung kết — BTC ban hành chính thức, 5 tiêu chí
  // I–V tổng 100đ (KHÁC Bán kết). Mirror BE ScoringService.get_final_rubric.
  const FINAL_CRITERIA = [
    {
      key: 'I_questioning',
      name: 'I. Kỹ năng hỏi (Questioning Skills)',
      description:
        'Câu hỏi ngắn gọn, trực diện, không mớm cung. Khai thác tình tiết chìa khóa (2,6 tỷ cổ tức; xâm phạm đời tư gắn định vị). Biết điểm dừng.',
      max_score: 15,
      weight: 1,
    },
    {
      key: 'II_argument_quality',
      name: 'II. Chất lượng Bản luận cứ và Tranh luận',
      description:
        'Bao quát đủ 3 vấn đề (hôn nhân, nuôi con, tài sản). Lập luận chia tài sản xuất sắc, đánh giá đúng công sức/lỗi; căn cứ toán học chính xác.',
      max_score: 30,
      weight: 1,
    },
    {
      key: 'III_response_handling',
      name: 'III. Kỹ năng đối đáp và xử lý tình huống',
      description:
        'Trả lời trực diện câu hỏi hóc búa từ HĐXX; tóm lỗ hổng đối phương bẻ gãy tại tòa; phản xạ tư duy nhanh, sắc bén.',
      max_score: 25,
      weight: 1,
    },
    {
      key: 'IV_legal_knowledge',
      name: 'IV. Kiến thức pháp lý (Legal Knowledge)',
      description:
        'Trích dẫn chuẩn xác Luật HN&GĐ, BLTTDS, BLDS (quyền riêng tư). Hiểu rõ "tài sản chung hợp nhất", "công sức tạo lập", nguyên tắc bảo vệ phụ nữ & trẻ em.',
      max_score: 15,
      weight: 1,
    },
    {
      key: 'V_conduct_teamwork',
      name: 'V. Tác phong, ngôn ngữ và làm việc nhóm',
      description:
        'Ngôn ngữ cơ thể đĩnh đạc, từ ngữ pháp lý chuẩn mực; phân bổ thời gian hợp lý giữa 2 Luật sư; tôn trọng HĐXX và đồng nghiệp.',
      max_score: 15,
      weight: 1,
    },
  ]
  const FINAL_RUBRIC: Rubric = {
    id: FINAL_RUBRIC_ID,
    name: 'Phiếu chấm Vòng Chung kết — Phiên xét xử',
    description:
      'Tổng 100 điểm, 5 tiêu chí I–V. HĐXX + BGK chấm trực tiếp, không weight, không AI gợi ý.',
    criteria: FINAL_CRITERIA,
  }

  // Get selected rubric — nếu là sentinel ID → trả built-in (không cần load BE).
  const selectedRubric: Rubric | undefined =
    scoringForm.rubric_id === INTRO_VIDEO_RUBRIC_ID
      ? INTRO_VIDEO_RUBRIC
      : scoringForm.rubric_id === SEMIFINAL_RUBRIC_ID
      ? SEMIFINAL_RUBRIC
      : scoringForm.rubric_id === FINAL_RUBRIC_ID
      ? FINAL_RUBRIC
      : scoringForm.rubric_id === R1_RUBRIC_ID
      ? R1_RUBRIC
      : rubrics.find((r) => r.id === scoringForm.rubric_id)

  // 2026-05-25: rubric Bán kết/Chung kết tắt AI suggest theo yêu cầu BTC
  // ("Hệ thống chỉ cộng dồn tổng: không auto-calculate trọng số, không AI gợi ý").
  // 2026-05-26: cũng tắt cho R1 — admin chấm trực tiếp theo BTC spec, không AI.
  const isHearingBuiltinRubric =
    scoringForm.rubric_id === SEMIFINAL_RUBRIC_ID ||
    scoringForm.rubric_id === FINAL_RUBRIC_ID ||
    scoringForm.rubric_id === R1_RUBRIC_ID
  // Phiếu Memorial KHÔNG chấm tiêu chí Chứng cứ (evidence_*) — chỉ chấm Phần 1 Đơn khởi kiện.
  // Tiêu chí evidence_* được auto-score qua flow Chứng cứ riêng.
  // Phiếu Video (intro_video) chỉ dùng 3 tiêu chí built-in — không filter thêm.
  const rubricCriteria = (selectedRubric?.criteria || []).filter((c) => {
    if (scoringForm.submission_type === 'memorial') {
      return !String(c.key).startsWith('evidence_')
    }
    if (scoringForm.submission_type === 'evidence') {
      return String(c.key).startsWith('evidence_')
    }
    return true
  })

  // AC-7 fix (audit 2026-05-22): realtime validate rubric scores. Trả ra
  // tên tiêu chí + max khi vượt → UI hiển thị viền đỏ + disable submit.
  // BRD spec PHẦN 4 §5: "Nếu nhập lớn hơn max → viền đỏ + khóa nút Gửi điểm".
  const rubricOverflowErrors = useMemo(() => {
    const errors: Record<string, { value: number; max: number; name: string }> = {}
    for (const c of rubricCriteria) {
      const v = scoringForm.rubric_scores[c.key]
      if (typeof v === 'number' && v > c.max_score) {
        errors[c.key] = { value: v, max: c.max_score, name: c.name }
      }
    }
    return errors
  }, [rubricCriteria, scoringForm.rubric_scores])
  const hasRubricOverflow = Object.keys(rubricOverflowErrors).length > 0

  // Auto-fetch PDF preview khi dialog mở với Memorial + có memorial_id
  useEffect(() => {
    let cancelled = false
    let createdUrl: string | null = null
    const shouldPreview =
      scoringDialogOpen &&
      scoringForm.submission_type !== 'transcript' &&
      scoringForm.submission_type !== 'evidence' &&
      scoringForm.memorial_id
    if (!shouldPreview) {
      setMemorialPreviewUrl(null)
      setMemorialPreviewError(null)
      return
    }
    setMemorialPreviewLoading(true)
    setMemorialPreviewError(null)
    ;(async () => {
      try {
        const blob = await downloadMemorialAsPdf(scoringForm.memorial_id!).unwrap()
        if (cancelled) return
        createdUrl = window.URL.createObjectURL(blob)
        setMemorialPreviewUrl(createdUrl)
      } catch (e: any) {
        if (cancelled) return
        // BE trả Blob ngay cả khi lỗi (responseHandler='blob' ở downloadMemorialAsPdf).
        // Khi error → e.data là Blob chứa JSON {detail: ...} hoặc text raw → cần đọc ra.
        let detail: string | null = null
        if (e?.data instanceof Blob) {
          try {
            const text = await e.data.text()
            try {
              const parsed = JSON.parse(text)
              detail = parsed?.detail || parsed?.message || null
            } catch {
              detail = text || null
            }
          } catch {
            /* ignore */
          }
        }
        const status = e?.status
        const fallback = status === 404
          ? 'Không tìm thấy file đính kèm của bài nộp này — có thể đội chưa upload hoặc file đã bị xoá.'
          : status === 415 || status === 422
            ? 'Định dạng file không hỗ trợ xem trước — vui lòng tải file gốc về để kiểm tra.'
            : status >= 500
              ? 'Máy chủ không chuyển được file sang PDF — thử tải file gốc về để kiểm tra.'
              : 'Không tải được bản xem trước.'
        setMemorialPreviewError(detail || e?.error || fallback)
      } finally {
        if (!cancelled) setMemorialPreviewLoading(false)
      }
    })()
    return () => {
      cancelled = true
      if (createdUrl) window.URL.revokeObjectURL(createdUrl)
    }
  }, [scoringDialogOpen, scoringForm.submission_type, scoringForm.memorial_id, downloadMemorialAsPdf])

  const handleOpenScoringDialog = (
    caseId: string,
    teamId: string,
    submissionId?: string,
    submissionType: 'memorial' | 'transcript' | 'evidence' | 'intro_video' = 'memorial',
    // Bug fix 2026-05-22 — Dual flow: caller (R1DualTeamScoringPanel) truyền `side`.
    // 1 = scenario_a (HS1) · 2 = scenario_b (HS2). null khi không phải dual flow.
    side?: R1Side,
  ) => {
    const sideSubmissionIndex: 1 | 2 | null =
      side === 'scenario_a' ? 1 : side === 'scenario_b' ? 2 : null
    // Check if score already exists — Dual flow: phải match cả submission_index
    // để HS1 và HS2 KHÔNG bị nhầm lẫn (1 đội có 2 phiếu cùng submission_type='evidence').
    const existingScore = scores.find((s) => {
      // Dual flow: nếu side được truyền, score phải có submission_index khớp.
      if (sideSubmissionIndex != null) {
        if ((s as any).submission_index !== sideSubmissionIndex) return false
      }
      if (submissionType === 'memorial') {
        // Nếu có submissionId (memorial_id) → match chính xác. Nếu không → fallback theo team+type+index.
        if (submissionId) {
          return s.case_id === caseId && s.team_id === teamId && s.memorial_id === submissionId
        }
        return s.case_id === caseId && s.team_id === teamId && s.submission_type === 'memorial'
      } else if (submissionType === 'transcript') {
        return s.case_id === caseId && s.team_id === teamId && s.transcript_id === submissionId
      } else if (submissionType === 'intro_video') {
        return s.case_id === caseId && s.team_id === teamId && s.submission_type === 'intro_video'
      }
      // evidence: 1 phiếu chấm/đội (legacy) HOẶC 1 phiếu/đội/hồ-sơ (dual).
      return s.case_id === caseId && s.team_id === teamId && s.submission_type === 'evidence'
    })

    // Rubric mặc định cho intro_video — Điều 10.3 (3 tiêu chí 0-10đ, fixed).
    // Phía BE: ScoringService.get_rubric trả built-in cho ID này.
    const INTRO_VIDEO_RUBRIC_ID = '__builtin__intro_video__'
    // 2026-05-26: memorial/evidence (R1) → LUÔN dùng built-in R1 rubric
    // (8 tiêu chí với prefix evidence_) — KHÔNG pick custom DB rubric vì
    // DB rubric cũ thường không có evidence_ prefix → form CC trống.
    const defaultRubricId =
      submissionType === 'intro_video'
        ? INTRO_VIDEO_RUBRIC_ID
        : submissionType === 'memorial' || submissionType === 'evidence'
        ? R1_RUBRIC_ID
        : (rubrics.find((r) => r.case_id === caseId)?.id ||
           rubrics.find((r) => !r.case_id)?.id ||
           rubrics[0]?.id || '')

    // Spec PHẦN 4.1 — khi sửa phiếu R1 dual, kế thừa submission_index/representing_role
    // từ score hiện có. Khi tạo mới, để null (UI sẽ prompt nếu case dùng dual flow).
    const existingSubIdx = (existingScore?.submission_index ?? null) as 1 | 2 | null
    const existingRole = (existingScore?.representing_role ?? '') as 'plaintiff' | 'defendant' | ''

    if (existingScore) {
      // 2026-05-26: cho R1 memorial/evidence, FORCE built-in R1 rubric
      // (8 tiêu chí prefix tách rõ) dù phiếu cũ trỏ DB rubric. DB rubric cũ
      // không có evidence_ prefix → form CC trống, admin chấm lẫn lộn.
      const forcedR1 =
        submissionType === 'memorial' || submissionType === 'evidence'
          ? R1_RUBRIC_ID
          : null
      setEditingScore(existingScore)
      setScoringForm({
        case_id: caseId,
        team_id: teamId,
        memorial_id: submissionType === 'memorial' ? (submissionId || '') : '',
        transcript_id: submissionType === 'transcript' ? (submissionId || '') : '',
        submission_type: submissionType,
        rubric_id: forcedR1 || existingScore.rubric_id || defaultRubricId,
        rubric_scores: existingScore.rubric_scores || {},
        comments: existingScore.comments || '',
        submission_index: existingSubIdx,
        representing_role: existingRole,
      })
    } else {
      setEditingScore(null)
      // Dual flow: kế thừa submission_index từ side button đã bấm. Representing role:
      // scenario_a → đội đại diện Nguyên đơn A; scenario_b → Nguyên đơn B. UI sẽ
      // hiển thị label "Hồ sơ 1/2 — {tên claimant}" lấy từ case.
      setScoringForm({
        case_id: caseId,
        team_id: teamId,
        memorial_id: submissionType === 'memorial' ? (submissionId || '') : '',
        transcript_id: submissionType === 'transcript' ? (submissionId || '') : '',
        submission_type: submissionType,
        rubric_id: defaultRubricId,
        rubric_scores: {},
        comments: '',
        submission_index: sideSubmissionIndex,
        representing_role: '',
      })
    }

    setSelectedCaseId(caseId)
    setSelectedTeamId(teamId)
    setScoringDialogOpen(true)
    setAISuggestions(null)
    setAiPreliminary(null)
  }

  const handleCloseScoringDialog = () => {
    setScoringDialogOpen(false)
    setEditingScore(null)
    setScoringForm({
      case_id: '',
      team_id: '',
      memorial_id: '',
      transcript_id: '',
      submission_type: '',
      rubric_id: '',
      rubric_scores: {},
      comments: '',
      submission_index: null,
      representing_role: '',
    })
    setRagQuery('')
    setRagResults([])
    setAISuggestions(null)
    setTranscriptContent('')
    setAiPreliminary(null)
  }

  // Load transcript content when transcript_id changes
  useEffect(() => {
    if (scoringForm.transcript_id && scoringForm.submission_type === 'transcript') {
      // Only load if transcript exists in DB
      const transcriptDoc = transcripts.find(t => t.id === scoringForm.transcript_id)
      if (transcriptDoc) {
        handleLoadTranscriptContent(scoringForm.transcript_id)
      } else {
        setTranscriptContent('')
      }
    } else {
      setTranscriptContent('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoringForm.transcript_id, scoringForm.submission_type])

  // Bug fix 2026-05-24 — fetch intro-video qua stream endpoint với Authorization
  // header → blob ObjectURL. Trước đây gán `iv.storage_path` thẳng vào <video src>
  // → browser load path nội bộ thất bại → player đen 0:00.
  useEffect(() => {
    let revokedUrl: string | null = null
    let cancelled = false
    if (
      scoringForm.submission_type === 'intro_video' &&
      scoringForm.team_id &&
      scoringDialogOpen
    ) {
      const teamId = scoringForm.team_id
      const team: any = teams.find((tt) => tt.id === teamId)
      if (!team?.intro_video?.file_name) {
        setIntroVideoBlobUrl(null)
        setIntroVideoError(null)
        return
      }
      setIsLoadingIntroVideo(true)
      setIntroVideoError(null)
      ;(async () => {
        try {
          const res = await fetch(
            buildApiUrl(API_BASE_URL, `/teams/${teamId}/intro-video/stream`),
            { headers: token ? { Authorization: `Bearer ${token}` } : undefined },
          )
          if (!res.ok) {
            const msg =
              res.status === 404 ? 'Đội chưa có video giới thiệu hoặc file đã bị xoá.'
              : res.status === 403 ? 'Bạn không có quyền xem video của đội này.'
              : res.status === 401 ? 'Phiên đăng nhập hết hạn — vui lòng đăng nhập lại.'
              : res.status >= 500 ? 'Lỗi máy chủ — vui lòng thử lại sau.'
              : `Không tải được video (HTTP ${res.status}).`
            if (!cancelled) setIntroVideoError(msg)
            return
          }
          const blob = await res.blob()
          const url = URL.createObjectURL(blob)
          if (cancelled) {
            URL.revokeObjectURL(url)
            return
          }
          revokedUrl = url
          setIntroVideoBlobUrl(url)
        } catch (e: any) {
          if (!cancelled) {
            setIntroVideoError(e?.message?.includes('Failed to fetch')
              ? 'Mất kết nối mạng — vui lòng thử lại.'
              : e?.message || 'Không tải được video')
          }
        } finally {
          if (!cancelled) setIsLoadingIntroVideo(false)
        }
      })()
    } else {
      setIntroVideoBlobUrl(null)
      setIntroVideoError(null)
    }
    return () => {
      cancelled = true
      if (revokedUrl) URL.revokeObjectURL(revokedUrl)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoringForm.team_id, scoringForm.submission_type, scoringDialogOpen])

  const handleRAGSearch = async () => {
    if (!ragQuery.trim() || !scoringForm.case_id || !scoringForm.team_id) {
      return
    }
    setIsRAGSearching(true)
    try {
      const data = await searchDocumentsScoring({
        query: ragQuery,
        case_id: scoringForm.case_id,
        team_id: scoringForm.team_id,
        limit: 10,
        similarity_threshold: 0.7,
        case_weight: 0.5,
        team_weight: 0.5,
      }).unwrap()
      setRagResults(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('RAG search failed:', error)
    } finally {
      setIsRAGSearching(false)
    }
  }

  const handleGetAISuggestions = async () => {
    const memorialId = scoringForm.memorial_id
    if (!memorialId) {
      setSnackbar({
        open: true,
        message: 'AI gợi ý chỉ khả dụng cho memorial đã được nộp. Vui lòng chọn memorial.',
        severity: 'warning',
      })
      return
    }

    // Hard FE timeout 180s — BE đã có per-criterion 90s × parallel ~10-15s.
    // 180s là safety net cho RAG cold start (bge-m3 chưa warm) + cache miss.
    setIsLoadingAI(true)
    setAiElapsedSecs(0)
    const tickerId = window.setInterval(() => {
      setAiElapsedSecs((s) => s + 1)
    }, 1000)
    const cleanup = () => {
      window.clearInterval(tickerId)
      setIsLoadingAI(false)
      setAiElapsedSecs(0)
    }
    try {
      // Race với timeout 180s — RTK Query không cung cấp AbortController qua
      // unwrap() nên ta dùng Promise.race + manual timeout.
      const aiPromise = getAISuggestions(memorialId).unwrap()
      const timeoutPromise = new Promise<never>((_, reject) =>
        window.setTimeout(
          () => reject(new Error('AI scoring timeout sau 180s — thử lại sau hoặc chấm thủ công.')),
          180_000,
        ),
      )
      const result = (await Promise.race([aiPromise, timeoutPromise])) as Awaited<typeof aiPromise>
      setAISuggestions(result)

      // Auto-fill điểm vào modal Chấm điểm để rút ngắn thao tác — GK vẫn có
      // thể chỉnh từng tiêu chí trước khi Submit. Banner cảnh báo + chip "AI"
      // trên mỗi field để nhắc review (xem render bên dưới).
      if (result?.suggested_scores && selectedRubric?.criteria) {
        const newScores: Record<string, number> = { ...scoringForm.rubric_scores }
        let filled = 0
        for (const c of selectedRubric.criteria) {
          const v = result.suggested_scores[c.key]
          if (v !== undefined && v !== null) {
            newScores[c.key] = v
            filled += 1
          }
        }
        if (filled > 0) {
          setScoringForm({ ...scoringForm, rubric_scores: newScores })
        }
      }

      const conf = result.confidence ?? 0
      const filledCount = result?.suggested_scores
        ? Object.keys(result.suggested_scores).length
        : 0
      const sevText =
        conf >= 0.7
          ? `AI đã điền ${filledCount} tiêu chí (confidence cao). Hãy review từng điểm trước khi Submit.`
          : conf >= 0.4
          ? `AI đã điền ${filledCount} tiêu chí (confidence trung bình) — review kỹ trước khi Submit.`
          : `AI có ít dữ liệu để gợi ý (confidence thấp ${(conf * 100).toFixed(0)}%). Khuyến nghị chấm thủ công.`
      setSnackbar({
        open: true,
        message: sevText,
        severity: conf >= 0.7 ? 'success' : conf >= 0.4 ? 'info' : 'warning',
      })
    } catch (error: any) {
      const detail = error?.data?.detail || error?.message || 'Không xác định'
      setAISuggestions(null)
      setSnackbar({
        open: true,
        message: `Không thể lấy AI gợi ý: ${detail}. Vui lòng chấm thủ công hoặc thử lại.`,
        severity: 'error',
      })
    } finally {
      cleanup()
    }
  }

  // Áp dụng gợi ý cho 1 tiêu chí
  const applyAISuggestionForCriterion = (criterionKey: string) => {
    if (!aiSuggestions?.suggested_scores) return
    const val = aiSuggestions.suggested_scores[criterionKey]
    if (val === undefined) return
    setScoringForm({
      ...scoringForm,
      rubric_scores: { ...scoringForm.rubric_scores, [criterionKey]: val },
    })
    setSnackbar({
      open: true,
      message: `Đã áp dụng gợi ý AI cho tiêu chí (${val})`,
      severity: 'success',
    })
  }

  // Áp dụng tất cả gợi ý
  const applyAllAISuggestions = () => {
    if (!aiSuggestions?.suggested_scores || !selectedRubric?.criteria) return
    const newScores: Record<string, number> = { ...scoringForm.rubric_scores }
    selectedRubric.criteria.forEach((c) => {
      const v = aiSuggestions.suggested_scores[c.key]
      if (v !== undefined) newScores[c.key] = v
    })
    setScoringForm({ ...scoringForm, rubric_scores: newScores })
    setSnackbar({
      open: true,
      message: 'Đã áp dụng tất cả gợi ý AI vào phiếu điểm',
      severity: 'success',
    })
  }

  // Áp dụng 2 điểm Chứng cứ vào phiếu (evidence_collection 35đ + evidence_filtering 15đ)
  const applyAiEvidenceScores = () => {
    if (!aiEvidenceData) return
    const rubricKeys = new Set(selectedRubric?.criteria?.map((c) => c.key) || [])
    const newScores: Record<string, number> = { ...scoringForm.rubric_scores }
    const applied: string[] = []
    const skipped: string[] = []
    const tryApply = (key: string, score: number | undefined, label: string) => {
      if (typeof score !== 'number') return
      if (!rubricKeys.has(key)) {
        skipped.push(label)
        return
      }
      newScores[key] = score
      applied.push(`${label} ${score}đ`)
    }
    tryApply('evidence_collection', aiEvidenceData.criterion_evidence_collection?.score, 'Thu thập')
    tryApply('evidence_filtering', aiEvidenceData.criterion_evidence_filtering?.score, 'Sàng lọc')

    if (applied.length === 0) {
      setSnackbar({
        open: true,
        message:
          skipped.length > 0
            ? `Rubric đang chọn không có tiêu chí Chứng cứ (${skipped.join(', ')}). Đổi sang rubric HNGĐ-01.`
            : 'Không có điểm để áp dụng.',
        severity: 'warning',
      })
      return
    }
    setScoringForm({ ...scoringForm, rubric_scores: newScores })
    setSnackbar({
      open: true,
      message:
        `Đã áp dụng điểm Chứng cứ: ${applied.join(', ')}. GK có thể chỉnh tay.` +
        (skipped.length > 0 ? ` (Bỏ qua: ${skipped.join(', ')} vì không có trong rubric)` : ''),
      severity: 'success',
    })
  }

  // Tổng điểm theo đúng các tiêu chí ĐANG hiển thị cho loại phiếu chấm.
  // Memorial → chỉ sum Phần 1 (max 50đ). Evidence → sum Phần 2 (max 50đ).
  // Trả về điểm RAW theo thang max của tiêu chí (không chuẩn hoá về thang 10).
  const calculateTotalScore = () => {
    if (rubricCriteria.length === 0) return 0
    let total = 0
    rubricCriteria.forEach((criterion) => {
      const score = scoringForm.rubric_scores[criterion.key] || 0
      total += score
    })
    return total
  }

  // 2026-05-25 (R1 spec lock per BTC): mỗi bộ hồ sơ R1 = 100đ
  //   - Đơn khởi kiện (memorial): max 50đ
  //   - Chứng cứ (evidence): max 50đ
  //   - 1 bộ hồ sơ = 50 + 50 = 100đ
  //   - 2 bộ hồ sơ (dual flow) = 200đ tổng (xem _aggregate_r1_dual_ranking)
  // BTC tạo rubric memorial trong DB với criteria sum = 100 (sai spec) →
  // display max bị 100. Clamp lại theo spec để admin/judge thấy ceiling đúng.
  const R1_PART_MAX_SCORE = 50
  const getMaxScore = () => {
    if (rubricCriteria.length === 0) return 0
    const rubricMax = rubricCriteria.reduce((sum, c) => sum + c.max_score, 0)
    // Memorial / Evidence phiếu R1 → cap 50 dù rubric DB cho phép cao hơn.
    if (scoringForm.submission_type === 'memorial' || scoringForm.submission_type === 'evidence') {
      return Math.min(rubricMax, R1_PART_MAX_SCORE)
    }
    return rubricMax
  }

  // Phase 5 (2026-05-25): phiếu đã thuộc bảng điểm published trở nên bất biến
  // VĨNH VIỄN. Lock form input, ẩn unlock/delete button, autosave/AI auto-off.
  // `scoreFormLocked` cũ chỉ check status='submitted' (cho phép unlock); với
  // published_at set, BE _ensure_mutable reject mọi mutation (kể cả unlock admin)
  // → FE phải honor để không hiển thị Save button gây 400 confusing.
  const editingScorePublished = Boolean(
    (editingScore as any)?.published_at ||
    (editingScore as any)?.published_in_result_id,
  )
  const scoreFormLocked = editingScore?.status === 'submitted' || editingScorePublished

  // Build payload rubric_scores theo loại phiếu chấm.
  // Phiếu Chứng cứ: LUÔN auto-fill 2 key (evidence_collection + evidence_filtering)
  // từ aiEvidenceData — BE compute_evidence_auto_score chấm theo "chứng cứ đúng
  // vs chứng cứ bẫy", admin KHÔNG nhập tay. Trùng key với R1_RUBRIC built-in.
  // Phiếu Memorial: admin nhập tay 4 tiêu chí m_*. Hearing: GK nhập 5 tiêu chí.
  const buildSubmitPayload = (): { rubric_scores: Record<string, number>; aiSnapshot: any } | { error: string } => {
    if (scoringForm.submission_type === 'evidence') {
      if (!aiEvidenceData) {
        return {
          error:
            'Vui lòng bấm "Tải báo cáo" ở panel Đối chiếu trước khi gửi điểm — hệ thống chấm dựa trên báo cáo này.',
        }
      }
      return {
        rubric_scores: {
          evidence_collection: aiEvidenceData.criterion_evidence_collection?.score ?? 0,
          evidence_filtering: aiEvidenceData.criterion_evidence_filtering?.score ?? 0,
        },
        aiSnapshot: aiEvidenceData,
      }
    }
    // Memorial / hearing: validate đủ tiêu chí theo rubric. Fail FE trước
    // khi BE 400 để UX tốt hơn.
    const required = rubricCriteria
    const missing = required
      .map((c) => c.key)
      .filter((k) => {
        const v = scoringForm.rubric_scores[k]
        return v == null || (typeof v === 'number' && Number.isNaN(v))
      })
    if (missing.length > 0) {
      return {
        error:
          `Thiếu điểm cho ${missing.length}/${required.length} tiêu chí. Vui lòng nhập đủ trước khi gửi.`,
      }
    }
    if (Object.keys(scoringForm.rubric_scores).length === 0) {
      return { error: 'Vui lòng chấm điểm cho ít nhất một tiêu chí' }
    }
    return { rubric_scores: scoringForm.rubric_scores, aiSnapshot: aiSuggestions }
  }

  const handleSubmitScore = async () => {
    if (scoreFormLocked) {
      setSnackbar({
        open: true,
        message: 'Phiếu đã gửi chính thức. BTC có thể mở khóa để chỉnh sửa.',
        severity: 'warning',
      })
      return
    }
    const payload = buildSubmitPayload()
    if ('error' in payload) {
      setSnackbar({ open: true, message: payload.error, severity: 'warning' })
      return
    }

    try {
      if (editingScore) {
        await updateScore({
          id: editingScore.id,
          update: {
            rubric_scores: payload.rubric_scores,
            comments: scoringForm.comments,
            ai_suggestions: payload.aiSnapshot,
          },
        }).unwrap()
        setSnackbar({
          open: true,
          message: 'Cập nhật điểm thành công!',
          severity: 'success',
        })
      } else {
        await createScore({
          case_id: scoringForm.case_id,
          team_id: scoringForm.team_id,
          memorial_id: scoringForm.memorial_id || undefined,
          transcript_id: scoringForm.transcript_id || undefined,
          submission_type: scoringForm.submission_type || undefined,
          rubric_id: scoringForm.rubric_id || undefined,
          rubric_scores: payload.rubric_scores,
          comments: scoringForm.comments,
          submission_index: scoringForm.submission_index ?? undefined,
          representing_role: scoringForm.representing_role || undefined,
        }).unwrap()
        setSnackbar({
          open: true,
          message: 'Chấm điểm thành công!',
          severity: 'success',
        })
      }
      refetchScores()
      handleCloseScoringDialog()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Chấm điểm thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }

  const handleFinalizeScore = async () => {
    if (scoreFormLocked) {
      setSnackbar({
        open: true,
        message: 'Phiếu đã được gửi trước đó.',
        severity: 'info',
      })
      return
    }
    // AC-7 (audit 2026-05-22): confirm dialog trước khi finalize.
    // BRD: "Sau khi gửi, phiếu chấm sẽ bị khóa và bạn không thể tự chỉnh sửa."
    const ok = window.confirm(
      'Sau khi gửi, phiếu chấm sẽ bị khóa và bạn không thể tự chỉnh sửa. Bạn có chắc chắn muốn gửi?'
    )
    if (!ok) return
    const payload = buildSubmitPayload()
    if ('error' in payload) {
      setSnackbar({ open: true, message: payload.error, severity: 'warning' })
      return
    }
    try {
      let scoreId = editingScore?.id
      if (editingScore) {
        const updated = await updateScore({
          id: editingScore.id,
          update: {
            rubric_scores: payload.rubric_scores,
            comments: scoringForm.comments,
            ai_suggestions: payload.aiSnapshot,
          },
        }).unwrap()
        scoreId = updated.id
      } else {
        const created = await createScore({
          case_id: scoringForm.case_id,
          team_id: scoringForm.team_id,
          memorial_id: scoringForm.memorial_id || undefined,
          transcript_id: scoringForm.transcript_id || undefined,
          submission_type: scoringForm.submission_type || undefined,
          rubric_id: scoringForm.rubric_id || undefined,
          rubric_scores: payload.rubric_scores,
          comments: scoringForm.comments,
          ai_suggestions: payload.aiSnapshot || undefined,
          submission_index: scoringForm.submission_index ?? undefined,
          representing_role: scoringForm.representing_role || undefined,
        }).unwrap()
        scoreId = created.id
      }
      if (scoreId) {
        await submitScore(scoreId).unwrap()
      }
      setSnackbar({
        open: true,
        message: 'Đã gửi điểm chính thức. Phiếu đã khóa.',
        severity: 'success',
      })
      refetchScores()
      handleCloseScoringDialog()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Gửi điểm thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }

  // 2026-05-25: chuyển từ window.prompt() sang dedicated dialog.
  // Phiếu Bán kết/Chung kết → mở dialog có audit history + radio chọn
  // mode (unlock kỹ thuật vs reopen yêu cầu chấm lại) + reason field
  // multiline. Phiếu R1 (memorial/transcript/intro_video) — vẫn dùng
  // dialog cho consistency, nhưng reason optional.
  const handleUnlockScoreRow = (scoreId: string) => {
    const targetScore = scores.find((s) => s.id === scoreId)
    if (!targetScore) return
    setUnlockDialog({
      open: true,
      score: targetScore,
      mode: 'unlock',
      reason: '',
      submitting: false,
      errorMsg: '',
    })
  }

  const handleConfirmUnlockDialog = async () => {
    const { score, mode, reason } = unlockDialog
    if (!score) return
    const needsReason =
      mode === 'reopen' || score.round_type === 'semifinal' || score.round_type === 'final'
    const trimmed = (reason || '').trim()
    if (needsReason && !trimmed) {
      setUnlockDialog((d) => ({
        ...d,
        errorMsg:
          mode === 'reopen'
            ? 'Lý do là bắt buộc khi yêu cầu chấm lại (sẽ gửi notify cho GK).'
            : 'Phiếu Bán kết / Chung kết yêu cầu lý do để ghi audit log.',
      }))
      return
    }
    setUnlockDialog((d) => ({ ...d, submitting: true, errorMsg: '' }))
    try {
      if (mode === 'reopen') {
        await reopenScoreM({ id: score.id, reason: trimmed }).unwrap()
        setSnackbar({
          open: true,
          message:
            'Đã yêu cầu chấm lại — đã gửi notify cho GK và ghi audit log (action=score.reopen).',
          severity: 'success',
        })
      } else {
        await unlockScore({ id: score.id, reason: trimmed || undefined }).unwrap()
        setSnackbar({
          open: true,
          message: 'Đã mở khoá phiếu điểm — đã ghi audit log (action=score.unlock).',
          severity: 'success',
        })
      }
      refetchScores()
      setUnlockDialog({
        open: false,
        score: null,
        mode: 'unlock',
        reason: '',
        submitting: false,
        errorMsg: '',
      })
    } catch (error: any) {
      const detail = error?.data?.detail
      let msg: string
      if (typeof detail === 'string') msg = detail
      else if (Array.isArray(detail)) msg = detail.map((d: any) => d?.msg || JSON.stringify(d)).join('; ')
      else msg = error?.message || 'Lỗi không xác định'
      setUnlockDialog((d) => ({ ...d, submitting: false, errorMsg: msg }))
    }
  }

  const handleDeleteScore = async (scoreId: string) => {
    if (!confirm('Bạn có chắc muốn xóa điểm này?')) return

    try {
      await deleteScore(scoreId).unwrap()
      setSnackbar({
        open: true,
        message: 'Xóa điểm thành công!',
        severity: 'success',
      })
      refetchScores()
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: `Xóa điểm thất bại: ${error.data?.detail || error.message}`,
        severity: 'error',
      })
    }
  }

  if (!canScoreCases) {
    return (
      <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
        <Alert severity="error">
          Bạn không có quyền chấm điểm. Chỉ Judge và Secretary mới có quyền này.
        </Alert>
      </Container>
    )
  }

  // Hiển thị tất cả cuộc thi user có quyền truy cập (BE đã filter theo role:
  // judge chỉ thấy case mình được phân công; secretary/admin thấy toàn bộ).
  // KHÔNG lọc theo "có memorial/transcript đã nộp" — nếu lọc, judge sẽ không thấy
  // case mà đội chưa kịp nộp bài (BR-08-02), tưởng mình chưa được phân công.
  // Sort: case có submission trước, sau đó tới case rỗng (0/0).
  const casesWithSubmissions = [...cases].sort((a, b) => {
    const aHas = memorials.some((m) => m.case_id === a.id) || transcripts.some((t) => t.case_id === a.id)
    const bHas = memorials.some((m) => m.case_id === b.id) || transcripts.some((t) => t.case_id === b.id)
    if (aHas === bHas) return 0
    return aHas ? -1 : 1
  })

  // Calculate stats for each case
  const getCaseStats = (caseId: string) => {
    const caseMemorials = memorials.filter((m) => m.case_id === caseId)
    const caseTranscripts = transcripts.filter((t) => t.case_id === caseId)
    const caseScores = scores.filter((s) => s.case_id === caseId)

    // Memorial: dedupe theo team_id — 1 đội có thể có nhiều version (draft + chính
    // thức + lịch sử). Trên bảng tổng hợp chỉ cần 1 đơn/đội. Detail view sẽ chọn
    // bản is_official_submission để chấm.
    const memorialTeamIds = new Set(caseMemorials.map((m) => m.team_id))
    const totalMemorials = memorialTeamIds.size
    const scoredMemorialTeamIds = new Set(
      caseScores
        .filter((s) => s.submission_type === 'memorial')
        .map((s) => s.team_id),
    )
    const memorialsScored = scoredMemorialTeamIds.size

    // Transcript (Video tranh tụng — chỉ có ở Bán kết/Chung kết): dedupe theo team_id.
    const transcriptTeamIds = new Set(caseTranscripts.map((t) => t.team_id))
    const totalTranscripts = transcriptTeamIds.size
    const scoredTranscriptTeamIds = new Set(
      caseScores
        .filter((s) => s.submission_type === 'transcript')
        .map((s) => s.team_id),
    )
    const transcriptsScored = scoredTranscriptTeamIds.size

    // Evidence: mỗi đội thuộc case là 1 phiếu evidence cần chấm.
    // Đã chấm = scores submission_type='evidence' duy nhất theo team_id.
    const caseItem = cases.find((c) => c.id === caseId)
    const totalEvidence = caseItem?.assigned_team_ids?.length || 0
    const scoredEvidenceTeamIds = new Set(
      caseScores
        .filter((s) => s.submission_type === 'evidence')
        .map((s) => s.team_id),
    )
    const evidenceScored = scoredEvidenceTeamIds.size

    return {
      totalMemorials,
      totalTranscripts,
      totalEvidence,
      scoredMemorials: memorialsScored,
      scoredTranscripts: transcriptsScored,
      scoredEvidence: evidenceScored,
      pendingMemorials: Math.max(0, totalMemorials - memorialsScored),
      pendingTranscripts: Math.max(0, totalTranscripts - transcriptsScored),
      pendingEvidence: Math.max(0, totalEvidence - evidenceScored),
    }
  }

  // Handle case selection
  const handleSelectCase = (caseId: string) => {
    setSelectedCaseId(caseId)
    setSelectedTeamId('')
    setViewMode('caseDetail')
  }

  // Handle back to case list
  const handleBackToCaseList = () => {
    setViewMode('caseList')
    setSelectedCaseId('')
    setSelectedTeamId('')
  }

  // Sets các pool_doc_id đội chọn ĐÚNG / SAI — để highlight item trên Bản phụ lục.
  const pickedCorrectDocIds = new Set<string>(
    (aiEvidenceData?.criterion_evidence_collection?.picked_correct_items || []).map((d: any) => d.document_id),
  )
  const pickedDecoyDocIds = new Set<string>(
    (aiEvidenceData?.criterion_evidence_filtering?.picked_decoy_items || []).map((d: any) => d.document_id),
  )

  // Helper: extract pool_doc_id từ evidence.tags (gắn lúc pick từ pool).
  const getPoolDocId = (ev: any): string | null => {
    const tags = ev?.tags || []
    for (const t of tags) {
      if (typeof t === 'string' && t.startsWith('from_pool_document:')) {
        return t.split(':', 2)[1]?.trim() || null
      }
    }
    return null
  }

  // Trạng thái đối chiếu cho từng evidence item:
  // - 'correct': trong tập chuẩn (xanh)
  // - 'decoy':   trong tập nhiễu (cam)
  // - 'unlabeled': pool doc chưa được BTC tag (xám)
  // - null: chưa có báo cáo, hoặc evidence không có pool_doc_id
  const getEvidenceMatchStatus = (ev: any): 'correct' | 'decoy' | 'unlabeled' | null => {
    if (!aiEvidenceData) return null
    const poolId = getPoolDocId(ev)
    if (!poolId) return null
    if (pickedCorrectDocIds.has(poolId)) return 'correct'
    if (pickedDecoyDocIds.has(poolId)) return 'decoy'
    return 'unlabeled'
  }

  // Báo cáo đối chiếu Chứng cứ với đáp án — dùng chung cho cả Memorial + Transcript + Evidence scoring.
  // Hiển thị 2 tiêu chí: Thu thập 35đ (Recall × 35) + Sàng lọc 15đ (15 − 2đ/file nhiễu).
  const evidenceAuditPanel = (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: 'primary.main',
        borderWidth: 1,
        overflow: 'hidden',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 1.5,
          background: gradientBrand,
          color: 'common.white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assessment fontSize="small" />
            Đối chiếu Chứng cứ với đáp án BTC
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.92 }}>
            Tổng 50đ · Thu thập 35đ (Recall) · Sàng lọc 15đ (penalty −2đ/file nhiễu)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button
            size="small"
            variant="contained"
            disabled={aiEvidenceLoading}
            onClick={handleLoadAiEvidence}
            sx={{
              bgcolor: 'common.white',
              color: 'primary.main',
              fontWeight: 700,
              textTransform: 'none',
              '&:hover': { bgcolor: 'grey.100' },
            }}
            startIcon={aiEvidenceLoading ? <CircularProgress size={14} /> : null}
          >
            {aiEvidenceData ? 'Tải lại' : 'Tải báo cáo'}
          </Button>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2.5 }}>
        {!aiEvidenceData ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Assessment sx={{ fontSize: 56, color: 'primary.main', mb: 1.5 }} />
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 700, color: 'text.primary' }}>
              Chưa tải báo cáo đối chiếu
            </Typography>
            <Typography variant="body2" sx={{ maxWidth: 460, mx: 'auto', display: 'block', color: 'text.primary' }}>
              Bấm <strong>"Tải báo cáo"</strong> để hệ thống so chứng cứ đội nộp với pool đáp án BTC.
              Chứng cứ đúng/sai sẽ được đánh dấu trực tiếp trên Bản phụ lục bên trái.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* 3 Score cards */}
            <Grid container spacing={1.5}>
              {[
                {
                  label: 'Thu thập (Recall)',
                  score: aiEvidenceData.criterion_evidence_collection.score,
                  max: aiEvidenceData.criterion_evidence_collection.max_score,
                  color: 'success.main',
                  bg: 'success.lighter',
                  bgFallback: '#E8F5E9',
                  detail: `${aiEvidenceData.criterion_evidence_collection.picked_correct}/${aiEvidenceData.criterion_evidence_collection.total_correct} chứng cứ đúng`,
                },
                {
                  label: 'Sàng lọc nhiễu',
                  score: aiEvidenceData.criterion_evidence_filtering.score,
                  max: aiEvidenceData.criterion_evidence_filtering.max_score,
                  color: 'warning.main',
                  bg: 'warning.lighter',
                  bgFallback: '#FFF8E1',
                  detail:
                    aiEvidenceData.criterion_evidence_filtering.picked_decoy === 0
                      ? 'Không chọn nhầm file nhiễu nào'
                      : `Chọn nhầm ${aiEvidenceData.criterion_evidence_filtering.picked_decoy} bẫy → trừ ${aiEvidenceData.criterion_evidence_filtering.picked_decoy * 2}đ`,
                },
                {
                  label: 'Tổng điểm Chứng cứ',
                  score: aiEvidenceData.total_evidence_score,
                  max: 50,
                  color: 'primary.main',
                  bg: 'primary.lighter',
                  bgFallback: '#E3F2FD',
                  detail: 'Gợi ý hệ thống — GK chốt',
                  isPrimary: true,
                },
              ].map((card) => {
                const pct = card.max > 0 ? (card.score / card.max) * 100 : 0
                return (
                  <Grid item xs={12} sm={4} key={card.label}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        height: '100%',
                        borderColor: card.color,
                        borderWidth: card.isPrimary ? 2 : 1,
                        bgcolor: card.bgFallback,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {card.label}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mt: 0.5 }}>
                        <Typography variant="h4" fontWeight={800} sx={{ color: card.color, lineHeight: 1 }}>
                          {card.score}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={500}>
                          / {card.max}đ
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, pct)}
                        sx={{
                          mt: 1,
                          height: 6,
                          borderRadius: 1,
                          bgcolor: 'rgba(0,0,0,0.06)',
                          '& .MuiLinearProgress-bar': { bgcolor: card.color },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        {card.detail}
                      </Typography>
                    </Paper>
                  </Grid>
                )
              })}
            </Grid>

            {/* Summary line */}
            <Box
              sx={{
                p: 1.25,
                bgcolor: 'action.hover',
                borderRadius: 1,
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                fontSize: '0.85rem',
              }}
            >
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
                <strong>{aiEvidenceData.criterion_evidence_collection.total_correct}</strong> chứng cứ chuẩn
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main' }} />
                <strong>{aiEvidenceData.criterion_evidence_filtering.total_decoy}</strong> bẫy nhiễu
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'info.main' }} />
                Đội chọn <strong>{aiEvidenceData.total_picked}</strong> file
              </Typography>
              {aiEvidenceData.unpooled_uploads_count > 0 && (
                <Typography variant="body2" color="text.secondary">
                  · {aiEvidenceData.unpooled_uploads_count} file ngoài pool (GK xét tay)
                </Typography>
              )}
            </Box>

            {/* Missed correct */}
            {aiEvidenceData.criterion_evidence_collection.missed_correct?.length > 0 && (
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: 'error.main', mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  ❌ Bỏ sót {aiEvidenceData.criterion_evidence_collection.missed_correct.length} chứng cứ chuẩn
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {aiEvidenceData.criterion_evidence_collection.missed_correct.slice(0, 10).map((d: any) => (
                    <Chip key={d.document_id} size="small" label={d.title} color="error" variant="outlined" />
                  ))}
                  {aiEvidenceData.criterion_evidence_collection.missed_correct.length > 10 && (
                    <Chip
                      size="small"
                      label={`+${aiEvidenceData.criterion_evidence_collection.missed_correct.length - 10} khác`}
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>
            )}

            {/* Picked decoy */}
            {aiEvidenceData.criterion_evidence_filtering.picked_decoy_items?.length > 0 && (
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: 'warning.main', mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}
                >
                  ⚠️ Chọn nhầm {aiEvidenceData.criterion_evidence_filtering.picked_decoy_items.length} bẫy nhiễu (−{aiEvidenceData.criterion_evidence_filtering.picked_decoy * 2}đ)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {aiEvidenceData.criterion_evidence_filtering.picked_decoy_items.map((d: any) => (
                    <Chip key={d.document_id} size="small" label={d.title} color="warning" variant="outlined" />
                  ))}
                </Box>
              </Box>
            )}

            {/* All-good state */}
            {aiEvidenceData.criterion_evidence_collection.missed_correct?.length === 0 &&
              aiEvidenceData.criterion_evidence_filtering.picked_decoy_items?.length === 0 && (
                <Alert
                  severity="success"
                  variant="filled"
                  sx={{ py: 1, fontWeight: 600 }}
                >
                  Đội chọn đúng toàn bộ chứng cứ chuẩn và không dính bẫy nhiễu nào.
                </Alert>
              )}
          </Box>
        )}
      </Box>
    </Paper>
  )

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title="Hệ thống Chấm điểm"
        subtitle="Chấm Vòng sơ loại, bản ghi tranh tụng và quản lý rubric theo từng cuộc thi."
        icon={<Assessment />}
        actions={
          canScoreCases ? (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<Download />}
              onClick={async () => {
                try {
                  const blob = await exportScoresCsv().unwrap()
                  const url = window.URL.createObjectURL(blob)
                  const a = document.createElement('a')
                  a.href = url
                  a.download = 'scores_export.csv'
                  document.body.appendChild(a)
                  a.click()
                  window.URL.revokeObjectURL(url)
                  document.body.removeChild(a)
                } catch (e: any) {
                  alert(e?.data?.detail || e?.message || 'Xuất CSV thất bại')
                }
              }}
            >
              Xuất CSV phiếu điểm (phạm vi quyền)
            </Button>
          ) : null
        }
      />

      {viewMode === 'caseList' ? (
        /* View 1: Danh sách cuộc thi - Dạng bảng */
        <Paper sx={{ mt: 3 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Danh sách cuộc thi cần chấm điểm
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Click vào một cuộc thi để xem chi tiết các submissions cần chấm điểm
            </Typography>
          </Box>
          <TableContainer sx={(theme) => getTablePaperSx(theme)}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Cuộc thi</strong></TableCell>
                  <TableCell align="center"><strong>Đơn khởi kiện</strong></TableCell>
                  <TableCell align="center"><strong>Chứng cứ</strong></TableCell>
                  <TableCell align="center"><strong>Video tranh tụng</strong></TableCell>
                  <TableCell align="center"><strong>Tổng cần chấm</strong></TableCell>
                  <TableCell align="right"><strong>Thao tác</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {casesWithSubmissions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Alert severity="info" sx={{ my: 2 }}>
                        Bạn chưa được phân công chấm cuộc thi nào. Liên hệ Ban Thư ký nếu cần kiểm tra.
                      </Alert>
                    </TableCell>
                  </TableRow>
                ) : (
                  casesWithSubmissions.map((caseItem: any) => {
                    const stats = getCaseStats(caseItem.id)
                    const totalPending = stats.pendingMemorials + stats.pendingEvidence + stats.pendingTranscripts

                    return (
                      <TableRow
                        key={caseItem.id}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            cursor: 'pointer'
                          }
                        }}
                        onClick={() => handleSelectCase(caseItem.id)}
                      >
                        <TableCell>
                          <Typography variant="body1" fontWeight={500}>
                            {formatCaseDisplay(caseItem)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {stats.scoredMemorials}/{stats.totalMemorials}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {stats.scoredEvidence}/{stats.totalEvidence}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {stats.scoredTranscripts}/{stats.totalTranscripts}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight={600} color={totalPending > 0 ? 'error.main' : 'success.main'}>
                            {totalPending}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              handleSelectCase(caseItem.id)
                            }}
                          >
                            Xem chi tiết
                          </Button>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        /* View 2: Chi tiết submissions của cuộc thi đã chọn */
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton onClick={handleBackToCaseList} color="primary">
              <ArrowBack />
            </IconButton>
            <Typography variant="h5">
              {cases.find((c) => c.id === selectedCaseId) ? formatCaseDisplay(cases.find((c) => c.id === selectedCaseId)!) : 'Chi tiết chấm điểm'}
            </Typography>
          </Box>

          {publishedNotice.teamPublishedStage ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Đã công bố — phiếu chấm vòng cũ đã khoá.</strong>{' '}
              {publishedNotice.teamPublishedStage} Mọi thao tác tạo phiếu mới hoặc sửa phiếu chưa nộp ở các vòng đã chốt sẽ bị hệ thống từ chối.
            </Alert>
          ) : publishedNotice.hasPublished ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Cuộc thi này đã có ít nhất một vòng được công bố — các phiếu chấm thuộc vòng đã chốt sẽ bị khoá. Chọn đội cụ thể để xem trạng thái chi tiết.
            </Alert>
          ) : null}

          {/* Filter by Team */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Lọc theo đội thi</InputLabel>
              <Select
                value={selectedTeamId}
                label="Lọc theo đội thi"
                onChange={(e) => setSelectedTeamId(e.target.value)}
              >
                <MenuItem value="">Tất cả đội thi</MenuItem>
                {(() => {
                  const currentCase = cases.find((c) => c.id === selectedCaseId)
                  // Ưu tiên scope theo case.assigned_team_ids (đội được phân cho case).
                  // Fallback: union(memorials, transcripts) nếu case chưa có assigned_team_ids
                  // (giai đoạn migrate / dữ liệu cũ).
                  const assignedIds: string[] = Array.isArray((currentCase as any)?.assigned_team_ids)
                    ? (currentCase as any).assigned_team_ids
                    : []
                  const caseMemorials = memorials.filter((m) => m.case_id === selectedCaseId)
                  const caseTranscripts = transcripts.filter((t) => t.case_id === selectedCaseId)
                  const submittedIds = [...new Set([
                    ...caseMemorials.map((m) => m.team_id),
                    ...caseTranscripts.map((t) => t.team_id),
                  ])]
                  const allTeamIds = assignedIds.length > 0
                    ? assignedIds.filter((id) => submittedIds.includes(id))
                    : submittedIds

                  return allTeamIds.map((teamId) => {
                    const team = teams.find((t) => t.id === teamId)
                    return team ? (
                      <MenuItem key={teamId} value={teamId}>
                        {team.name} - {team.university}
                      </MenuItem>
                    ) : null
                  })
                })()}
              </Select>
            </FormControl>
          </Paper>

          {/* Spec PHẦN G3 (audit 2026-05-21) — Bảng chấm điểm 2 hồ sơ side-by-side
              cho case dual_claimant_packets. Mỗi đội 1 row với 2 thẻ HS1/HS2:
              trạng thái đơn + chứng cứ + điểm + nút chấm. */}
          {(() => {
            const cur: any = cases.find((c) => c.id === selectedCaseId)
            if (cur?.r1_flow_version !== 'dual_claimant_packets') return null
            return (
              <R1DualTeamScoringPanel
                caseItem={cur}
                teams={teams}
                memorials={memorials}
                evidence={caseEvidenceList as any}
                scores={scores}
                onOpenScoring={handleOpenScoringDialog}
                selectedTeamId={selectedTeamId}
                readOnly={currentUser?.role === 'judge'}
              />
            )
          })()}

          {/* Admin dashboard — Tiến độ chấm Bán kết / Chung kết (real-time poll 30s) */}
          {isAdminScoring && scorecardProgress.length > 0 && (
            <Paper sx={{ mb: 2 }}>
              <Box sx={{ p: 2, pb: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Tiến độ chấm điểm Bán kết / Chung kết
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Theo dõi real-time GK đã gửi N/total phiếu. Admin có thể nhập thay GK
                  chưa hoàn tất hoặc gặp sự cố thiết bị (M07 §3).
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Vòng</strong></TableCell>
                      <TableCell><strong>Giám khảo</strong></TableCell>
                      <TableCell><strong>Phòng</strong></TableCell>
                      <TableCell><strong>Tiến độ</strong></TableCell>
                      <TableCell><strong>Deadline</strong></TableCell>
                      <TableCell><strong>Trạng thái</strong></TableCell>
                      <TableCell align="right"><strong>Thao tác</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {scorecardProgress.map((p) => {
                      const judgeUser = judgeUsers.find((u: any) => u.id === p.judge_id)
                      const judgeName = judgeUser?.full_name || judgeUser?.username || 'Chưa xác định'
                      const dl = p.deadline_at ? new Date(p.deadline_at) : null
                      const hoursLeft = dl ? Math.floor((dl.getTime() - Date.now()) / 3600000) : null
                      // Tìm phiếu draft đầu tiên để gợi ý "Nhập thay"
                      const draftId = p.scorecard_ids.find((sid) => {
                        const s = scores.find((x) => x.id === sid)
                        return s && (s.status || 'submitted') === 'draft'
                      })
                      return (
                        <TableRow
                          key={`${p.courtroom_id}-${p.judge_id}-${p.round_type}`}
                          sx={p.overdue ? { backgroundColor: 'error.50' } : {}}
                        >
                          <TableCell>
                            <Chip
                              label={p.round_type === 'semifinal' ? 'Bán kết' : 'Chung kết'}
                              size="small"
                              color={p.round_type === 'semifinal' ? 'info' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {judgeName}
                            </Typography>
                            {p.any_via_proxy && (
                              <Chip
                                label={`${p.via_proxy_count} phiếu nhập thay`}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 0, mt: 0.5, height: 18, fontSize: 10 }}
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {p.courtroom_id ? p.courtroom_id.slice(0, 8) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={600}>
                                {p.submitted}/{p.total}
                              </Typography>
                              {p.draft > 0 && (
                                <Chip label={`${p.draft} nháp`} size="small" color="warning" sx={{ height: 18, fontSize: 10 }} />
                              )}
                              {p.untouched > 0 && (
                                <Chip label={`${p.untouched} chưa mở`} size="small" color="default" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            {dl ? (
                              <Tooltip title={dl.toLocaleString('vi-VN')}>
                                <Typography
                                  variant="caption"
                                  color={p.overdue ? 'error.main' : (hoursLeft != null && hoursLeft < 6 ? 'warning.main' : 'text.secondary')}
                                  fontWeight={600}
                                >
                                  {p.overdue
                                    ? `Quá hạn ${Math.abs(hoursLeft || 0)}h`
                                    : hoursLeft != null
                                      ? `Còn ${hoursLeft}h`
                                      : '—'}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {p.submitted === p.total ? (
                              <Chip label="Hoàn tất" size="small" color="success" />
                            ) : p.overdue ? (
                              <Chip label="QUÁ HẠN" size="small" color="error" />
                            ) : (
                              <Chip label="Đang chấm" size="small" color="warning" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {draftId ? (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  const s = scores.find((x) => x.id === draftId)
                                  if (s) openProxyDialog(s)
                                }}
                              >
                                Nhập thay
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* 2026-05-25: CTA "Chốt điểm vòng" — khi mọi GK đã submit phiếu,
                  hiển thị banner xanh + button sang Reports để publish-match. */}
              {(() => {
                // Derive: complete = đã submit đủ phiếu cho cặp (submitted >= total).
                const isRowComplete = (p: any) =>
                  (p.submitted ?? 0) >= (p.total ?? 0) && (p.total ?? 0) > 0
                const allComplete =
                  scorecardProgress.length > 0 &&
                  scorecardProgress.every(isRowComplete)
                if (!allComplete) {
                  const pending = scorecardProgress.filter((p) => !isRowComplete(p)).length
                  return (
                    <Alert severity="info" sx={{ m: 2, mt: 0 }}>
                      Còn <b>{pending}</b> phiếu chấm chưa hoàn tất. Sau khi
                      mọi GK gửi đủ phiếu → admin sẽ thấy nút "Sang Reports
                      công bố cặp" ở đây.
                    </Alert>
                  )
                }
                return (
                  <Alert
                    severity="success"
                    icon={<CheckCircle />}
                    sx={{ m: 2, mt: 0 }}
                    action={
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<Assessment />}
                        onClick={() => {
                          const url = selectedCaseId
                            ? `/reports?case=${selectedCaseId}`
                            : '/reports'
                          window.location.href = url
                        }}
                      >
                        Sang Reports → Công bố winner
                      </Button>
                    }
                  >
                    <b>Đã chốt điểm.</b> Tất cả {scorecardProgress.length} phiếu
                    chấm Bán kết / Chung kết đã được GK gửi. Bước tiếp theo:
                    sang <b>Báo cáo &amp; Phân tích</b> → mục "Cặp đấu Bán
                    kết" → chọn winner theo điểm / theo cặp → bấm "Công bố
                    [Team] thắng" cho từng cặp đấu. Sau khi 2 cặp đều công bố
                    → hệ thống tự chuyển sang Vòng Chung kết.
                  </Alert>
                )
              })()}
            </Paper>
          )}

          {/* 2026-05-25: Phiếu chấm Bán kết / Chung kết của tôi.
              Section riêng cho judge — list scorecard auto-spawn từ
              `ScoringService.auto_create_scorecards_for_courtroom` (gọi khi
              courtroom end). Mỗi (judge × team × match) = 1 phiếu, status
              ban đầu 'draft', deadline T+48h cho R2, T+2h cho R3.
              Section ẨN cho admin (đã có view khác) và khi không có phiếu nào. */}
          {!isAdminScoring && (() => {
            const myCourtroomScores = scores.filter(
              (s) =>
                s.submission_type === 'courtroom' &&
                (s.round_type === 'semifinal' || s.round_type === 'final') &&
                s.judge_id === currentUser?.id,
            )
            if (myCourtroomScores.length === 0) return null
            // Filter theo case nếu có chọn — giúp judge tập trung 1 phiên.
            const visible = selectedCaseId
              ? myCourtroomScores.filter((s) => s.case_id === selectedCaseId)
              : myCourtroomScores
            return (
              <Paper sx={{ mb: 3 }}>
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Phiếu chấm Bán kết / Chung kết của bạn
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Phiếu được tự tạo khi phiên xử kết thúc. Nhập điểm theo
                    rubric + bấm "Gửi phiếu" trước deadline. Sau khi gửi phiếu
                    sẽ khóa, liên hệ BTC nếu cần unlock.
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Vòng</strong></TableCell>
                          <TableCell><strong>Cuộc thi</strong></TableCell>
                          <TableCell><strong>Đội</strong></TableCell>
                          <TableCell align="center"><strong>Trạng thái</strong></TableCell>
                          <TableCell align="right"><strong>Tổng điểm</strong></TableCell>
                          <TableCell><strong>Deadline</strong></TableCell>
                          <TableCell align="right"><strong>Thao tác</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {visible.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <Alert severity="info" sx={{ my: 1 }}>
                                Không có phiếu nào cho cuộc thi đang chọn.
                                {!selectedCaseId
                                  ? ''
                                  : ' Chọn cuộc thi khác hoặc bỏ chọn để xem tất cả.'}
                              </Alert>
                            </TableCell>
                          </TableRow>
                        ) : (
                          visible.map((sc) => {
                            const caseItem = cases.find((c) => c.id === sc.case_id)
                            const team = teams.find((t) => t.id === sc.team_id)
                            const submitted = sc.status === 'submitted'
                            const published = !!(sc as any).published_at
                            const deadline = (sc as any).deadline_at
                              ? new Date((sc as any).deadline_at)
                              : null
                            const overdue =
                              deadline && !submitted && deadline.getTime() < Date.now()
                            return (
                              <TableRow key={sc.id} hover>
                                <TableCell>
                                  <Chip
                                    size="small"
                                    label={sc.round_type === 'final' ? 'Chung kết' : 'Bán kết'}
                                    color={sc.round_type === 'final' ? 'error' : 'info'}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>
                                    {caseItem ? formatCaseDisplay(caseItem) : sc.case_id}
                                  </Typography>
                                </TableCell>
                                <TableCell>{team?.name || '— Đội không xác định —'}</TableCell>
                                <TableCell align="center">
                                  {published ? (
                                    <Chip size="small" color="success" label="Đã công bố" />
                                  ) : submitted ? (
                                    <Chip size="small" color="warning" label="Đã gửi" />
                                  ) : (
                                    <Chip size="small" color="default" variant="outlined" label="Nháp" />
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={700}>
                                    {(sc.total_score ?? 0).toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {deadline ? (
                                    <Typography
                                      variant="caption"
                                      color={overdue ? 'error' : 'text.secondary'}
                                    >
                                      {deadline.toLocaleString('vi-VN')}
                                      {overdue && ' (quá hạn)'}
                                    </Typography>
                                  ) : (
                                    '—'
                                  )}
                                </TableCell>
                                <TableCell align="right">
                                  <Button
                                    size="small"
                                    variant={submitted ? 'outlined' : 'contained'}
                                    startIcon={submitted ? <Edit /> : <Assessment />}
                                    onClick={() => {
                                      // 2026-05-25 (LOCK SPEC): Semifinal /
                                      // Chung kết ALWAYS dùng built-in rubric
                                      // BTC chính thức (5 tiêu chí, max 100đ).
                                      // KHÔNG load sc.rubric_id (có thể trỏ
                                      // custom DB rubric cũ → form sai). Memorial
                                      // dùng rubric_id của score như cũ.
                                      const forcedRubricId =
                                        sc.round_type === 'final'
                                          ? FINAL_RUBRIC_ID
                                          : sc.round_type === 'semifinal'
                                          ? SEMIFINAL_RUBRIC_ID
                                          : sc.rubric_id || ''
                                      setEditingScore(sc)
                                      setScoringForm({
                                        case_id: sc.case_id,
                                        team_id: sc.team_id,
                                        memorial_id: '',
                                        transcript_id: '',
                                        submission_type: 'transcript',
                                        rubric_id: forcedRubricId,
                                        rubric_scores: sc.rubric_scores || {},
                                        comments: sc.comments || '',
                                        submission_index: null,
                                        representing_role: '',
                                      })
                                      setScoringDialogOpen(true)
                                    }}
                                  >
                                    {published ? 'Xem phiếu' : submitted ? 'Sửa / Xem' : 'Chấm điểm'}
                                  </Button>
                                </TableCell>
                              </TableRow>
                            )
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            )
          })()}

          {/* Submissions Table (Memorials + Transcripts) */}
          <Paper>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Danh sách cần chấm điểm
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Bao gồm: Memorial (vòng chấm viết) và Video tranh tụng online (vòng tranh tụng)
              </Typography>
              {/* YC16 (audit 2026-05-23): filter loại + trạng thái chấm + sort điểm */}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Loại</InputLabel>
                  <Select
                    label="Loại"
                    value={filterSubType}
                    onChange={(e) => setFilterSubType(e.target.value as any)}
                  >
                    <MenuItem value="">Tất cả</MenuItem>
                    <MenuItem value="memorial">Đơn khởi kiện</MenuItem>
                    <MenuItem value="evidence">Chứng cứ</MenuItem>
                    <MenuItem value="transcript">Video tranh tụng</MenuItem>
                    <MenuItem value="intro_video">Video giới thiệu</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Trạng thái</InputLabel>
                  <Select
                    label="Trạng thái"
                    value={filterScoreStatus}
                    onChange={(e) => setFilterScoreStatus(e.target.value as any)}
                  >
                    <MenuItem value="all">Tất cả</MenuItem>
                    <MenuItem value="unscored">Chưa chấm</MenuItem>
                    <MenuItem value="scored">Đã chấm</MenuItem>
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Sắp xếp điểm</InputLabel>
                  <Select
                    label="Sắp xếp điểm"
                    value={sortByScore}
                    onChange={(e) => setSortByScore(e.target.value as any)}
                  >
                    <MenuItem value="">Mặc định</MenuItem>
                    <MenuItem value="desc">Điểm cao → thấp</MenuItem>
                    <MenuItem value="asc">Điểm thấp → cao</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Box>
        <TableContainer sx={(theme) => getTablePaperSx(theme)}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Loại</strong></TableCell>
                <TableCell><strong>Cuộc thi</strong></TableCell>
                <TableCell><strong>Đội thi</strong></TableCell>
                <TableCell><strong>File</strong></TableCell>
                <TableCell><strong>Kích thước</strong></TableCell>
                <TableCell><strong>Điểm</strong></TableCell>
                <TableCell><strong>Ngày chấm</strong></TableCell>
                <TableCell align="right"><strong>Thao tác</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingTeams ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : (() => {
                // Combine memorials, transcripts, evidence (locks) — chỉ dữ liệu thật từ DB.
                // Evidence row sinh từ submission lock (đội đã nộp chính thức).
                //
                // Bug fix 2026-05-25 — Dedup evidence locks per (case, team, r1_side).
                // Lý do: trước khi enforce dual flow (Phase B 2026-05-22), một số team
                // có lock LEGACY với `r1_side=null`. Sau migration, BE tạo thêm lock
                // mới với r1_side='scenario_a' / 'scenario_b' nhưng KHÔNG xoá lock
                // legacy → mỗi team xuất hiện 3 dòng "Chứng cứ" thay vì 2 (HS1+HS2).
                // Quy tắc dedup:
                //  - Group theo (case_id, team_id).
                //  - Nếu có lock với r1_side cụ thể → giữ những lock đó, drop lock legacy
                //    (r1_side null) để không trùng.
                //  - Nếu chỉ có lock legacy → giữ 1.
                //  - Trong cùng (case, team, r1_side) → chỉ giữ lock mới nhất (theo locked_at).
                const _locksByKey = new Map<string, typeof evidenceLocks[number]>()
                for (const l of evidenceLocks) {
                  if (!l.locked) continue
                  const sideKey = l.r1_side ?? 'null'
                  const k = `${l.case_id}::${l.team_id}::${sideKey}`
                  const prev = _locksByKey.get(k)
                  if (!prev) {
                    _locksByKey.set(k, l)
                  } else {
                    const prevAt = prev.locked_at || ''
                    const curAt = l.locked_at || ''
                    if (curAt > prevAt) _locksByKey.set(k, l)
                  }
                }
                // Drop legacy lock khi đã có lock side-specific cho cùng (case, team).
                const _teamHasSideLock = new Set<string>()
                for (const l of _locksByKey.values()) {
                  if (l.r1_side) _teamHasSideLock.add(`${l.case_id}::${l.team_id}`)
                }
                const _dedupedLocks = Array.from(_locksByKey.values()).filter((l) => {
                  if (l.r1_side) return true
                  return !_teamHasSideLock.has(`${l.case_id}::${l.team_id}`)
                })
                const evidenceRows = _dedupedLocks.map((l) => ({
                  // Include r1_side trong id để React key unique + dual-flow phân biệt.
                  id: `evidence-${l.case_id}-${l.team_id}-${l.r1_side || 'legacy'}`,
                  case_id: l.case_id,
                  team_id: l.team_id,
                  r1_side: l.r1_side || null,
                  type: 'evidence' as const,
                  title: l.r1_side === 'scenario_a'
                    ? 'Hồ sơ chứng cứ (HS1)'
                    : l.r1_side === 'scenario_b'
                    ? 'Hồ sơ chứng cứ (HS2)'
                    : 'Hồ sơ chứng cứ chính thức',
                  file_name: l.total_items != null ? `${l.total_items} mục` : '',
                  submitted_at: l.locked_at || undefined,
                }))
                // Phiếu Video giới thiệu Sơ loại (Điều 10.3) — GK chấm 3 tiêu chí.
                // 1 đội đã nộp intro_video → tạo 1 row chấm. Spec:
                // docs/operations/Dac_ta_Cham_diem_So_ket_video.md
                // Bug fix 2026-05-24 — iterate ALL teams có intro_video (giống memorial).
                // Trước đây chỉ lấy assigned_team_ids → case chưa gán đội (hoặc gán rỗng)
                // sẽ KHÔNG hiện row nào, dù đội đã nộp video. Filter assignment được
                // áp dụng ở bước sau (assignedTeamIdsForTable check) — chỉ active khi
                // case có assigned_team_ids > 0, giữ behavior nhất quán với memorial.
                const introVideoRows = teams
                  .map((t) => {
                    const iv = (t as any)?.intro_video
                    if (!iv?.file_name) return null
                    return {
                      id: `intro-video-${selectedCaseId}-${t.id}`,
                      case_id: selectedCaseId,
                      team_id: t.id,
                      type: 'intro_video' as const,
                      title: 'Video giới thiệu (Sơ loại)',
                      file_name: iv.file_name || 'Video',
                      file_size: iv.file_size || 0,
                      submitted_at: iv.uploaded_at,
                    }
                  })
                  .filter(Boolean) as any[]
                // Memorial: chỉ tính BẢN CHÍNH THỨC (is_official_submission).
                // - Judge: ENFORCED — không có bản chính thức thì không hiện row (UAT
                //   feedback Hình 2: GK chấm được cả bản draft sẽ chấm sai bài).
                // - Admin/BTC: fallback hiển thị version mới nhất để pre-screen / nhập thay
                //   trong giai đoạn migrate / dữ liệu cũ chưa lock.
                // Bug fix 2026-05-22 — Dual flow: mỗi đội có 2 memorial (scenario_a + scenario_b),
                // cả hai đều is_official_submission=true. Key phải include r1_side để
                // KHÔNG bị ghi đè → "Danh sách cần chấm điểm" hiển thị đủ 2 dòng "Đơn khởi kiện".
                // Legacy single role: r1_side null → key có suffix "::null" (1 đội = 1 memorial).
                const memorialKey = (m: any) => `${m.case_id}::${m.team_id}::${(m as any).r1_side ?? 'null'}`
                const officialMemorialsByTeam = new Map<string, any>()
                for (const m of memorials) {
                  if (m.is_official_submission) {
                    officialMemorialsByTeam.set(memorialKey(m), m)
                  }
                }
                const memorialsForScoring = memorials.filter((m) => {
                  const key = memorialKey(m)
                  const official = officialMemorialsByTeam.get(key)
                  if (official) return m.id === official.id
                  // Không có bản chính thức cho slot này:
                  if (!isAdminScoring) return false // Judge: bỏ qua draft
                  // Admin/BTC: fallback version mới nhất CÙNG SLOT (case+team+r1_side).
                  const teamVersions = memorials
                    .filter((x) => memorialKey(x) === key)
                    .sort((a, b) => (a.submitted_at || '') > (b.submitted_at || '') ? -1 : 1)
                  return teamVersions[0]?.id === m.id
                })

                const currentCaseForTable = cases.find((c) => c.id === selectedCaseId)
                const assignedTeamIdsForTable: string[] = Array.isArray(
                  (currentCaseForTable as any)?.assigned_team_ids,
                )
                  ? (currentCaseForTable as any).assigned_team_ids
                  : []
                const allSubmissions: any[] = [
                  ...memorialsForScoring.map(m => ({ ...m, type: 'memorial' as const })),
                  ...transcripts.map(t => ({ ...t, type: 'transcript' as const })),
                  ...evidenceRows,
                  ...introVideoRows,
                ].filter((item) => {
                  if (item.case_id !== selectedCaseId) return false
                  if (selectedTeamId && item.team_id !== selectedTeamId) return false
                  // YC16: filter theo loại submission
                  if (filterSubType && item.type !== filterSubType) return false
                  // Khi xem "Tất cả đội thi", chỉ hiện submissions thuộc đội được phân vào case
                  // (assigned_team_ids). Fallback: nếu case chưa gán đội (dữ liệu cũ) — hiện tất cả.
                  if (
                    !selectedTeamId
                    && assignedTeamIdsForTable.length > 0
                    && (!item.team_id || !assignedTeamIdsForTable.includes(item.team_id))
                  ) {
                    return false
                  }
                  return true
                })
                // YC16: filter theo trạng thái chấm (matchingScore lookup) + sort điểm.
                // Helper inline để tránh duplicate logic ở row render.
                // Bug fix 2026-05-24 — Dual flow (HS1+HS2): score có memorial_id thì BẮT BUỘC
                // match strict theo memorial_id. OR-fallback cũ làm row HS1 và HS2 share
                // cùng score → unlock nhầm phiếu, "Hồ sơ 1 không chỉnh được" sau unlock.
                // Fallback theo case+team+type chỉ áp dụng cho score legacy (memorial_id=null).
                const _getMatchingScoreRow = (item: any) => scores.filter((s) => {
                  if (item.type === 'memorial') {
                    if (s.memorial_id) return s.memorial_id === item.id
                    return s.case_id === item.case_id && s.team_id === item.team_id && s.submission_type === 'memorial'
                  } else if (item.type === 'transcript') {
                    if (s.transcript_id) return s.transcript_id === item.id
                    return s.case_id === item.case_id && s.team_id === item.team_id && s.submission_type === 'transcript'
                  } else if (item.type === 'intro_video') {
                    return s.case_id === item.case_id && s.team_id === item.team_id && s.submission_type === 'intro_video'
                  }
                  return s.case_id === item.case_id && s.team_id === item.team_id && s.submission_type === 'evidence'
                })[0]
                let _filtered = allSubmissions
                if (filterScoreStatus !== 'all') {
                  _filtered = _filtered.filter((it: any) => {
                    const sc = _getMatchingScoreRow(it)
                    return filterScoreStatus === 'scored' ? !!sc : !sc
                  })
                }
                if (sortByScore) {
                  _filtered = [..._filtered].sort((a: any, b: any) => {
                    const sa = _getMatchingScoreRow(a)?.total_score ?? -1
                    const sb = _getMatchingScoreRow(b)?.total_score ?? -1
                    return sortByScore === 'asc' ? sa - sb : sb - sa
                  })
                }
                // Reassign for downstream code that references allSubmissions
                ;(allSubmissions as any).length = 0
                ;(allSubmissions as any).push(..._filtered)

                if (allSubmissions.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Alert severity="info" sx={{ my: 2 }}>
                          Chưa có bài nộp nào cần chấm điểm.
                        </Alert>
                      </TableCell>
                    </TableRow>
                  )
                }

                return allSubmissions.map((item) => {
                  const caseItem = cases.find((c) => c.id === item.case_id)
                  const team = teams.find((t) => t.id === item.team_id)
                  // Fallback: nếu teams chưa load xong → "Đang tải...", nếu team đã bị xoá
                  // hoặc không nằm trong scope của GK → "(Đội không xác định)" thay vì raw ID khó hiểu.
                  const teamName = team
                    ? team.name
                    : (isLoadingTeams ? 'Đang tải...' : '(Đội không xác định)')
                  
                  // Admin nhìn tất cả phiếu của tất cả GK; judge chỉ thấy phiếu của
                  // chính mình. Group scores theo submission để hiển thị từng GK chấm bao
                  // nhiêu (UAT feedback: BTC cần thấy ai chấm bao nhiêu để rà soát chênh
                  // lệch điểm trước khi công bố).
                  // Bug fix 2026-05-24 — strict match theo memorial_id/transcript_id khi
                  // score đã gắn id. OR-fallback cũ làm 2 row HS1/HS2 share cùng score,
                  // dẫn đến nút unlock target sai phiếu → "Hồ sơ 1 không chỉnh được"
                  // sau khi unlock (thực tế đang unlock phiếu HS2).
                  // 2026-05-26 fix CRITICAL: lookup phải scope theo r1_side (HS1/HS2).
                  // Trước đây evidence row HS2 lookup không có submission_index check
                  // → match HS1 evidence score → admin "Sửa CC HS2" thực sự ghi đè
                  // HS1 → 2 hồ sơ hiện cùng điểm. Tương tự memorial fallback.
                  const itemR1Side: 'scenario_a' | 'scenario_b' | null =
                    ((item as any).r1_side === 'scenario_a' || (item as any).r1_side === 'scenario_b')
                      ? (item as any).r1_side
                      : null
                  const itemSubIdx: 1 | 2 | null =
                    itemR1Side === 'scenario_a' ? 1 : itemR1Side === 'scenario_b' ? 2 : null
                  const matchingScores = scores.filter((s) => {
                    // Dual flow: nếu item có r1_side cụ thể → score phải có
                    // submission_index khớp. Score legacy (sub_idx null) KHÔNG match
                    // row dual → ép admin tạo phiếu mới đúng slot.
                    if (itemSubIdx != null) {
                      if ((s as any).submission_index !== itemSubIdx) return false
                    }
                    if (item.type === 'memorial') {
                      if (s.memorial_id) return s.memorial_id === item.id
                      return s.case_id === item.case_id && s.team_id === item.team_id && s.submission_type === 'memorial'
                    } else if (item.type === 'transcript') {
                      if (s.transcript_id) return s.transcript_id === item.id
                      return s.case_id === item.case_id && s.team_id === item.team_id && s.submission_type === 'transcript'
                    } else if (item.type === 'intro_video') {
                      return s.case_id === item.case_id && s.team_id === item.team_id && s.submission_type === 'intro_video'
                    }
                    return s.case_id === item.case_id && s.team_id === item.team_id && s.submission_type === 'evidence'
                  })
                  const existingScore = matchingScores[0]
                  const judgeNameById = (jid?: string) => {
                    if (!jid) return ''
                    const u = judgeUsers.find((x: any) => x.id === jid)
                    return u?.full_name || u?.username || u?.email || 'Chưa xác định'
                  }

                  return (
                    <TableRow
                      key={`${item.type}-${item.id}`}
                      sx={{
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          cursor: 'pointer'
                        }
                      }}
                    >
                      <TableCell>
                        {item.type === 'evidence' ? (
                          <Chip
                            label="Chứng cứ"
                            color="success"
                            size="small"
                            icon={<AttachFile />}
                          />
                        ) : item.type === 'intro_video' ? (
                          <Chip
                            label="Video giới thiệu"
                            color="warning"
                            size="small"
                            icon={<VideoLibrary />}
                            title="Phiếu Video Sơ loại (Điều 10.3) — 3 tiêu chí, max 30đ"
                          />
                        ) : (
                          <Chip
                            label={item.type === 'memorial' ? 'Đơn khởi kiện' : 'Video tranh tụng'}
                            color={item.type === 'memorial' ? 'primary' : 'secondary'}
                            size="small"
                            icon={item.type === 'memorial' ? <Description /> : <VideoLibrary />}
                            sx={{
                              fontWeight: item.type === 'transcript' ? 600 : 'normal',
                              border: item.type === 'transcript' ? 2 : 0,
                              borderColor: item.type === 'transcript' ? 'secondary.main' : 'transparent'
                            }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {caseItem ? formatCaseDisplay(caseItem) : item.case_id}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {teamName}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {item.type === 'memorial' ? item.file_name : (item.title || item.file_name || 'Video tranh tụng')}
                          </Typography>
                          {item.type === 'memorial' && item.is_official_submission && (
                            <Chip
                              label="Chính thức"
                              size="small"
                              color="success"
                              sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
                            />
                          )}
                          {item.type === 'memorial' && !item.is_official_submission && (
                            <Chip
                              label={`v${item.version || 1} draft`}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, fontSize: 10 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {item.file_size > 0 ? `${(item.file_size / 1024 / 1024).toFixed(2)} MB` : '-'}
                      </TableCell>
                      <TableCell>
                        {isAdminScoring && matchingScores.length > 1 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            {matchingScores
                              .slice()
                              .sort((a, b) => (b.total_score || 0) - (a.total_score || 0))
                              .map((s) => (
                                <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                  <Chip
                                    label={(s.total_score || 0).toFixed(2)}
                                    color={s.status === 'submitted' ? 'success' : 'default'}
                                    size="small"
                                    sx={{ height: 20, fontSize: 11 }}
                                  />
                                  <Typography variant="caption" sx={{ minWidth: 70, fontWeight: 600 }}>
                                    {judgeNameById(s.judge_id)}
                                  </Typography>
                                  {s.status !== 'submitted' && (
                                    <Chip label="Nháp" color="default" size="small" variant="outlined"
                                      sx={{ height: 18, fontSize: 9 }} />
                                  )}
                                </Box>
                              ))}
                          </Box>
                        ) : existingScore ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
                            <Chip
                              label={existingScore.total_score.toFixed(2)}
                              color="success"
                              size="small"
                            />
                            {isAdminScoring && existingScore.judge_id && (
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                ({judgeNameById(existingScore.judge_id)})
                              </Typography>
                            )}
                            {existingScore.status === 'submitted' && !(existingScore as any).published_at && (
                              <Chip label="Đã gửi" color="warning" size="small" variant="outlined" />
                            )}
                            {(existingScore as any).published_at && (
                              <Chip
                                label="Đã công bố"
                                color="success"
                                size="small"
                                title="Phiếu thuộc bảng điểm đã công bố — bất biến vĩnh viễn."
                                sx={{ height: 20, fontSize: 10, fontWeight: 700 }}
                              />
                            )}
                            {existingScore.round_type === 'semifinal' && (
                              <Chip label="Bán kết" color="info" size="small" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                            )}
                            {existingScore.round_type === 'final' && (
                              <Chip label="Chung kết" color="error" size="small" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
                            )}
                            {existingScore.via_proxy && (
                              <Chip
                                label="Nhập thay"
                                size="small"
                                color="default"
                                sx={{ height: 20, fontSize: 10, fontWeight: 600 }}
                                title={`Admin nhập thay GK (${existingScore.proxy_reason || 'other'})`}
                              />
                            )}
                            {existingScore.deadline_at && existingScore.status !== 'submitted' && (() => {
                              const dl = new Date(existingScore.deadline_at)
                              const diffMs = dl.getTime() - Date.now()
                              const hours = Math.floor(diffMs / 3600000)
                              const overdue = diffMs < 0
                              return (
                                <Chip
                                  label={overdue ? `Quá hạn ${Math.abs(hours)}h` : `Còn ${hours}h`}
                                  size="small"
                                  color={overdue ? 'error' : (hours < 6 ? 'warning' : 'default')}
                                  sx={{ height: 20, fontSize: 10 }}
                                  title={`Deadline: ${dl.toLocaleString('vi-VN')}`}
                                />
                              )
                            })()}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Chưa chấm
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {existingScore?.scored_at ? (
                          <Typography variant="body2">
                            {new Date(existingScore.scored_at).toLocaleString('vi-VN')}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {/* 2026-05-25: Judge KHÔNG chấm R1 (memorial/
                              evidence/intro_video) theo spec BTC. Chỉ cho
                              phép xem phiếu nếu đã có (status=submitted).
                              Bỏ Chấm/Sửa cho judge ở các loại R1. */}
                          {(() => {
                            const isJudgeRole = currentUser?.role === 'judge'
                            const isR1Item =
                              item.type === 'memorial' ||
                              item.type === 'evidence' ||
                              item.type === 'intro_video'
                            const judgeR1ReadOnly = isJudgeRole && isR1Item
                            if (judgeR1ReadOnly && !existingScore) {
                              return (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontStyle: 'italic', pr: 1 }}
                                >
                                  GK chỉ xem — chưa có điểm
                                </Typography>
                              )
                            }
                            return (
                              <Button
                                size="small"
                                variant={existingScore ? 'outlined' : 'contained'}
                                color={item.type === 'transcript' ? 'secondary' : 'primary'}
                                startIcon={existingScore ? <Edit /> : <Assessment />}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  // 2026-05-26 fix: truyền r1_side để dialog match
                                  // đúng phiếu HS1/HS2 (chống ghi đè cross-hồ-sơ).
                                  handleOpenScoringDialog(
                                    item.case_id || '',
                                    item.team_id || '',
                                    item.id,
                                    item.type,
                                    itemR1Side || undefined,
                                  )
                                }}
                                disabled={judgeR1ReadOnly && existingScore?.status !== 'submitted'}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 500,
                                  borderRadius: 1,
                                }}
                              >
                                {judgeR1ReadOnly
                                  ? 'Xem phiếu'
                                  : existingScore
                                  ? existingScore.status === 'submitted'
                                    ? 'Xem phiếu'
                                    : 'Sửa điểm'
                                  : 'Chấm điểm'}
                              </Button>
                            )
                          })()}
                          {/* Phase 5 (2026-05-25): ẩn unlock cho phiếu đã
                              published — BE _ensure_mutable reject mọi unlock
                              (kể cả admin) sau khi mark_score_published. */}
                          {existingScore &&
                            existingScore.status === 'submitted' &&
                            !(existingScore as any).published_at &&
                            currentUser &&
                            hasPermission(currentUser.role, 'MANAGE_USERS') && (
                              <IconButton
                                size="small"
                                color="primary"
                                title="Mở khóa để chỉnh sửa"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleUnlockScoreRow(existingScore.id)
                                }}
                                disabled={isUnlocking}
                              >
                                <LockOpen fontSize="small" />
                              </IconButton>
                            )}
                          {/* Phase 5: ẩn delete cho phiếu published. */}
                          {existingScore &&
                            !(existingScore as any).published_at &&
                            (existingScore.status !== 'submitted' ||
                              (currentUser &&
                                hasPermission(currentUser.role, 'MANAGE_USERS'))) && (
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteScore(existingScore.id)
                              }}
                              title="Xóa điểm"
                              sx={{
                                border: '1px solid',
                                borderColor: 'error.main',
                                borderRadius: 1,
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                })
              })()}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
        </Box>
      )}

      {/* Scoring Dialog */}
      <Dialog 
        open={scoringDialogOpen} 
        onClose={handleCloseScoringDialog} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: { xs: '100dvh', sm: '95dvh', md: '95dvh' },
            height: { xs: '100dvh', sm: '95dvh', md: '95dvh' },
            m: { xs: 0, sm: 2 },
            borderRadius: { xs: 0, sm: 1 },
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Assessment />
            <Typography variant="h6">Chấm điểm</Typography>
            {scoringForm.submission_type === 'transcript' && (
              <Chip label="Video tranh tụng" color="secondary" size="small" sx={{ ml: 1 }} />
            )}
            {scoreFormLocked && (
              <Chip label="Đã gửi (khóa)" color="warning" size="small" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 0 }}>
          {scoringForm.submission_type === 'transcript' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
              {/* Hàng trên: Cuộc thi, Đội thi, Danh sách tham dự - Tỷ lệ 3/3/6 */}
              <Paper sx={{ 
                p: { xs: 1, sm: 1.5 }, 
                backgroundColor: 'grey.50', 
                mx: { xs: 1, sm: 2 }, 
                mt: { xs: 1, sm: 2 }, 
                mb: { xs: 1, sm: 2 } 
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'flex-start', md: 'flex-start' }, 
                  gap: { xs: 1.5, md: 2 } 
                }}>
                  <Box sx={{ 
                    flex: { xs: '1 1 100%', md: '0 0 25%' }, 
                    maxWidth: { xs: '100%', md: '25%' },
                    width: { xs: '100%', md: 'auto' }
                  }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Cuộc thi
                    </Typography>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {cases.find((c) => c.id === scoringForm.case_id)?.title || '— Cuộc thi chưa rõ —'}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                  <Divider sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }} />
                  <Box sx={{ 
                    flex: { xs: '1 1 100%', md: '0 0 25%' }, 
                    maxWidth: { xs: '100%', md: '25%' },
                    width: { xs: '100%', md: 'auto' }
                  }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Đội thi
                    </Typography>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      {(() => {
                        const team = teams.find((t) => t.id === scoringForm.team_id)
                        if (team) return team.name
                        // Audit 2026-05-21: KHÔNG show raw team_id cho judge.
                        return isLoadingTeams ? 'Đang tải...' : '— Đội chưa xác định —'
                      })()}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                  <Divider sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }} />
                  <Box sx={{ 
                    flex: { xs: '1 1 100%', md: '0 0 50%' }, 
                    maxWidth: { xs: '100%', md: '50%' },
                    width: { xs: '100%', md: 'auto' }
                  }}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Danh sách tham dự
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {(() => {
                        const currentTeam = teams.find(t => t.id === scoringForm.team_id)
                        const otherTeams = teams.filter(t => t.id !== scoringForm.team_id)
                        
                        const participants = [
                          { role: 'Thẩm phán', name: 'Nguyễn Văn A' },
                          { role: 'Nguyên đơn', name: currentTeam ? currentTeam.name : 'N/A' },
                          { role: 'Bị đơn', name: otherTeams[0] ? otherTeams[0].name : 'Đối thủ' },
                          { role: 'Thư ký', name: 'Trần Thị B' },
                          { role: 'Quan sát viên', name: 'Lê Văn C' }
                        ]
                        
                        return participants.map((p, idx) => (
                          <Chip 
                            key={idx}
                            label={`${p.role}: ${p.name}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '20px' }}
                          />
                        ))
                      })()}
                    </Box>
                  </Box>
                </Box>
              </Paper>

              {/* Bên dưới: Chia 2 cột trái/phải */}
              <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ 
                flex: 1, 
                overflow: 'hidden', 
                m: 0, 
                px: { xs: 1, sm: 2 }, 
                pb: { xs: 1, sm: 2 } 
              }}>
                {/* Cột trái: Video + Transcript */}
                <Grid item xs={12} md={6} sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  overflow: 'hidden', 
                  height: { 
                    xs: 'auto', 
                    sm: 'calc(95dvh - 250px)', 
                    md: 'calc(95dvh - 200px)' 
                  },
                  minHeight: { xs: '400px', sm: 'auto' }
                }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2 }, height: '100%', overflow: 'auto' }}>

                  {/* Video Player */}
                  <Paper sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                      <VideoLibrary fontSize="small" />
                      Video Tranh Tụng
                    </Typography>
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%', // 16:9 aspect ratio
                        backgroundColor: 'grey.900',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      {(() => {
                        const transcriptDoc = transcripts.find(t => t.id === scoringForm.transcript_id)
                        const videoUrl = transcriptDoc?.description || ''
                        const isYouTube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')

                        // Không có URL thật → empty state. KHÔNG fallback video dummy.
                        if (!videoUrl) {
                          return (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'grey.400',
                                px: 2,
                                textAlign: 'center',
                              }}
                            >
                              <Typography variant="body2">
                                Chưa có bản ghi video buổi tranh tụng.
                              </Typography>
                            </Box>
                          )
                        }

                        if (!isYouTube) {
                          return (
                            <video
                              controls
                              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                              src={videoUrl}
                            >
                              Trình duyệt của bạn không hỗ trợ video tag.
                            </video>
                          )
                        }

                        const ytMatch = videoUrl.match(/^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/)
                        const videoId = ytMatch && ytMatch[2].length === 11 ? ytMatch[2] : null
                        if (!videoId) {
                          return (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'grey.400',
                                px: 2,
                                textAlign: 'center',
                              }}
                            >
                              <Typography variant="body2">URL video không hợp lệ.</Typography>
                            </Box>
                          )
                        }
                        return (
                          <iframe
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                            src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            title="Video tranh tụng"
                          />
                        )
                      })()}
                    </Box>
                  </Paper>

                  {/* Transcript Viewer */}
                  <Paper sx={{ p: { xs: 1.5, sm: 2 }, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: { xs: '300px', sm: 0 } }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 1, sm: 1.5 } }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                        <Article fontSize="small" />
                        Transcript
                      </Typography>
                      {(() => {
                        const transcriptDoc = transcripts.find(t => t.id === scoringForm.transcript_id)
                        
                        return transcriptDoc ? (
                          <Button
                            size="small"
                            startIcon={<Description />}
                            onClick={() => handleDownloadTranscript(transcriptDoc.id, transcriptDoc.file_name)}
                          >
                            Tải Transcript
                          </Button>
                        ) : null
                      })()}
                    </Box>
                    <Box
                      sx={{
                        flex: 1,
                        overflowY: 'auto',
                        p: 2,
                        backgroundColor: 'grey.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      {(() => {
                        const transcriptDoc = transcripts.find(t => t.id === scoringForm.transcript_id)
                        
                        // If transcript not found in DB, show transcript content
                        if (!transcriptDoc) {
                          const defaultTranscriptContent = `TRANSCRIPT - VÒNG TRANH TỤNG ONLINE

Thời gian: 10:00 - 12:00, Ngày ${new Date().toLocaleDateString('vi-VN')}
Cuộc thi: ${cases.find(c => c.id === scoringForm.case_id)?.title || 'N/A'}
Đội thi: ${teams.find(t => t.id === scoringForm.team_id)?.name || 'N/A'}

=== PHẦN MỞ ĐẦU ===

[10:00:15] Thẩm phán: Phiên tòa xét xử vụ án được mở. Mời các bên trình bày.

[10:01:30] Nguyên đơn: Kính thưa Hội đồng xét xử, chúng tôi xin trình bày yêu cầu của mình...

[10:05:45] Bị đơn: Kính thưa Hội đồng, chúng tôi phản đối yêu cầu của nguyên đơn vì...

=== PHẦN TRANH TỤNG ===

[10:15:20] Thẩm phán: Mời nguyên đơn trình bày chứng cứ.

[10:16:10] Nguyên đơn: Chúng tôi xin đưa ra các chứng cứ sau:
1. Hợp đồng ký ngày 15/01/2024
2. Biên bản giao nhận hàng hóa
3. Email trao đổi giữa hai bên

[10:25:30] Bị đơn: Chúng tôi phản đối chứng cứ số 3 vì email đó không có chữ ký điện tử...

[10:30:15] Thẩm phán: Mời nguyên đơn phản hồi.

[10:31:00] Nguyên đơn: Email đó được gửi từ địa chỉ chính thức của công ty bị đơn...

=== PHẦN KẾT LUẬN ===

[11:45:00] Thẩm phán: Các bên có còn ý kiến gì nữa không?

[11:45:30] Nguyên đơn: Không, thưa Hội đồng.

[11:45:35] Bị đơn: Không, thưa Hội đồng.

[11:46:00] Thẩm phán: Phiên tòa tạm nghỉ. Hội đồng sẽ xem xét và đưa ra quyết định.

=== KẾT THÚC ===

[12:00:00] Phiên tòa kết thúc.`

                          return (
                            <Box>
                              <Typography variant="body2" fontWeight={600} gutterBottom>
                                Transcript - Vòng Tranh Tụng Online
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                  fontSize: '0.875rem',
                                  lineHeight: 1.6,
                                }}
                              >
                                {defaultTranscriptContent}
                              </Typography>
                            </Box>
                          )
                        }
                        
                        // Hiển thị transcript content từ DB
                        return (
                          <Box>
                            <Typography variant="body2" fontWeight={600} gutterBottom>
                              {transcriptDoc.title}
                            </Typography>
                            {isLoadingTranscript ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <CircularProgress size={24} />
                              </Box>
                            ) : transcriptContent ? (
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  whiteSpace: 'pre-wrap',
                                  fontFamily: '"Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif',
                                  fontSize: '0.875rem',
                                  lineHeight: 1.6,
                                }}
                              >
                                {transcriptContent}
                              </Typography>
                            ) : transcriptDoc.description ? (
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {transcriptDoc.description}
                              </Typography>
                            ) : (
                              <Alert severity="info">
                                <Typography variant="body2">
                                  Transcript đã được upload. Nội dung đang được tải...
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                  File: {transcriptDoc.file_name} ({(transcriptDoc.file_size / 1024 / 1024).toFixed(2)} MB)
                                </Typography>
                              </Alert>
                            )}
                          </Box>
                        )
                      })()}
                    </Box>
                  </Paper>
                </Box>
              </Grid>

                {/* Cột phải: Form chấm điểm + AI Assistive */}
                <Grid item xs={12} md={6} sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  overflow: 'hidden', 
                  height: { 
                    xs: 'auto', 
                    sm: 'calc(95dvh - 250px)', 
                    md: 'calc(95dvh - 200px)' 
                  },
                  maxHeight: { 
                    xs: 'none', 
                    sm: 'calc(95dvh - 250px)', 
                    md: 'calc(95dvh - 200px)' 
                  },
                  minHeight: { xs: '400px', sm: 'auto' }
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: { xs: 1.5, sm: 2 }, 
                    overflow: 'auto', 
                    pr: { xs: 0, sm: 1 }, 
                    height: '100%', 
                    minHeight: 0, 
                    pb: { xs: 1, sm: 2 }, 
                    m: { xs: 0, sm: '5px' } 
                  }}>
                  {/* Rubric Selection */}
                  {rubrics.length > 0 && (
                    <FormControl sx={{ mt: '5px' }} fullWidth>
                      <InputLabel id="rubric-select-label">Rubric chấm điểm</InputLabel>
                      <Select
                        value={scoringForm.rubric_id}
                        label="Rubric chấm điểm"
                        labelId="rubric-select-label"
                        onChange={(e) => {
                          const rubricId = e.target.value
                          setScoringForm({
                            ...scoringForm,
                            rubric_id: rubricId,
                            // Reset scores when changing rubric
                            rubric_scores: {},
                          })
                        }}
                      >
                        {rubrics
                          .filter((r) => !r.case_id || r.case_id === scoringForm.case_id)
                          .map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                              {r.name}
                              {r.case_id && r.case_id === scoringForm.case_id && (
                                <Chip label="Cuộc thi này" size="small" sx={{ ml: 1 }} />
                              )}
                              {!r.case_id && (
                                <Chip label="Chung" size="small" color="primary" sx={{ ml: 1 }} />
                              )}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* AI Assistive Button.
                      2026-05-25: ẨN cho rubric Bán kết/Chung kết built-in
                      theo yêu cầu BTC ("không AI gợi ý"). */}
                  {canUseAiAssistive && !isHearingBuiltinRubric && (
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={<AutoAwesome />}
                      onClick={handleGetAISuggestions}
                      disabled={isLoadingAI || scoreFormLocked}
                      fullWidth
                      sx={{
                        textTransform: 'none',
                        fontWeight: 500,
                        py: 1.5,
                      }}
                    >
                      {isLoadingAI ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={20} />
                          <Typography variant="body2">
                            Đang chấm... {aiElapsedSecs}s
                            {aiElapsedSecs > 30 && ' (RAG cold start, chờ thêm chút)'}
                          </Typography>
                        </Box>
                      ) : 'AI Assistive'}
                    </Button>
                    {aiSuggestions && (
                      <Alert
                        severity={
                          (aiSuggestions.confidence ?? 0) >= 0.7
                            ? 'success'
                            : (aiSuggestions.confidence ?? 0) >= 0.4
                            ? 'info'
                            : 'warning'
                        }
                        sx={{ mt: 2 }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            🤖 Phân tích AI sơ bộ
                          </Typography>
                          {aiSuggestions.suggested_scores &&
                            Object.keys(aiSuggestions.suggested_scores).length > 0 && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={applyAllAISuggestions}
                                disabled={scoreFormLocked}
                                sx={{ textTransform: 'none' }}
                              >
                                Áp dụng tất cả
                              </Button>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                          {aiSuggestions.analysis}
                        </Typography>
                        {aiSuggestions.recommendations && aiSuggestions.recommendations.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                              Đề xuất:
                            </Typography>
                            <Box component="ul" sx={{ margin: 0, paddingLeft: 3, mt: 1 }}>
                              {aiSuggestions.recommendations.map((rec: string, idx: number) => (
                                <li key={idx}>
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>{rec}</Typography>
                                </li>
                              ))}
                            </Box>
                          </Box>
                        )}
                        {aiSuggestions.confidence != null && (
                          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              Độ tin cậy:
                            </Typography>
                            <Box
                              sx={{
                                flex: 1,
                                height: 6,
                                borderRadius: 1,
                                background: '#E0E0E0',
                                overflow: 'hidden',
                                maxWidth: 180,
                              }}
                            >
                              <Box
                                sx={{
                                  width: `${(aiSuggestions.confidence * 100).toFixed(0)}%`,
                                  height: '100%',
                                  background:
                                    aiSuggestions.confidence >= 0.7
                                      ? '#2E7D32'
                                      : aiSuggestions.confidence >= 0.4
                                      ? '#1565C0'
                                      : '#EF6C00',
                                }}
                              />
                            </Box>
                            <Typography variant="caption" fontWeight={600}>
                              {(aiSuggestions.confidence * 100).toFixed(0)}%
                            </Typography>
                          </Box>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
                          AI chỉ gợi ý — Giám khảo có toàn quyền quyết định điểm cuối (P5-AI-2).
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                  )}

                  <Divider />

                  {evidenceAuditPanel}

                  {/* Rubric Scores */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Điểm theo Rubric
                    </Typography>
                    {rubricCriteria.length > 0 ? (
                      rubricCriteria.map((criterion) => {
                        const aiVal = aiSuggestions?.suggested_scores?.[criterion.key]
                        const judgeVal = scoringForm.rubric_scores[criterion.key]
                        const diverged =
                          aiVal !== undefined &&
                          judgeVal !== undefined &&
                          Math.abs(judgeVal - aiVal) >= 1.0
                        return (
                        <Box key={criterion.key} sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={500}>
                              {criterion.name}
                            </Typography>
                            {criterion.description && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {criterion.description}
                              </Typography>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                              {criterion.weight && criterion.weight !== 1.0 && (
                                <Chip label={`Weight: ${criterion.weight}`} size="small" />
                              )}
                              {aiVal !== undefined && (
                                <Tooltip
                                  title={
                                    aiSuggestions?.per_criterion_reasoning?.[criterion.key]
                                      ? `🤖 ${aiSuggestions.per_criterion_reasoning[criterion.key]}`
                                      : 'Click để áp dụng điểm AI gợi ý vào ô bên phải.'
                                  }
                                  placement="top"
                                  arrow
                                >
                                  <Chip
                                    label={`🤖 AI: ${aiVal.toFixed(1)}`}
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    onClick={
                                      scoreFormLocked
                                        ? undefined
                                        : () => applyAISuggestionForCriterion(criterion.key)
                                    }
                                    sx={{ cursor: scoreFormLocked ? 'default' : 'pointer' }}
                                  />
                                </Tooltip>
                              )}
                              {diverged && (
                                <Chip
                                  label={`Lệch ${(judgeVal - aiVal).toFixed(1)}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
                            <TextField
                              type="number"
                              value={scoringForm.rubric_scores[criterion.key] ?? ''}
                              onChange={(e) => {
                                // AC-7: KHÔNG auto-clamp — để user thấy lỗi vượt max.
                                const raw = e.target.value
                                const value = raw === '' ? undefined : Math.max(0, parseFloat(raw) || 0)
                                setScoringForm({
                                  ...scoringForm,
                                  rubric_scores: {
                                    ...scoringForm.rubric_scores,
                                    [criterion.key]: value as any,
                                  },
                                })
                              }}
                              disabled={scoreFormLocked}
                              error={!!rubricOverflowErrors[criterion.key]}
                              helperText={
                                rubricOverflowErrors[criterion.key]
                                  ? `Tối đa ${rubricOverflowErrors[criterion.key].max} điểm`
                                  : ''
                              }
                              inputProps={{
                                min: 0,
                                max: criterion.max_score,
                                step: 0.1,
                                style: { textAlign: 'right', padding: '8px' }
                              }}
                              size="small"
                              sx={{
                                width: 80,
                                '& .MuiOutlinedInput-input': {
                                  textAlign: 'right'
                                }
                              }}
                              placeholder="0"
                            />
                            <Typography variant="body1" color="text.secondary">
                              / {criterion.max_score}
                            </Typography>
                          </Box>
                        </Box>
                      )})
                    ) : (
                      <Alert severity="warning">
                        Chưa có rubric nào. Vui lòng chọn rubric hoặc tạo rubric mới.
                      </Alert>
                    )}
                  </Box>

                  <Divider />

                  {/* Total Score */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Tổng điểm: {calculateTotalScore().toFixed(2)} / {getMaxScore().toFixed(1)}
                    </Typography>
                    {editingScore && (
                      <Typography variant="caption" color="text.secondary">
                        Điểm cũ: {editingScore.total_score.toFixed(2)} / 10
                      </Typography>
                    )}
                  </Box>

                  {/* Comments */}
                  <TextField
                    fullWidth
                    label="Nhận xét"
                    value={scoringForm.comments}
                    onChange={(e) => setScoringForm({ ...scoringForm, comments: e.target.value })}
                    multiline
                    rows={4}
                    disabled={scoreFormLocked}
                    placeholder="Nhập nhận xét về video tranh tụng..."
                  />
                </Box>
                </Grid>
              </Grid>
            </Box>
          ) : scoringForm.submission_type === 'evidence' ? (
            /* ═══ Evidence Scoring Layout — 3 rows: Meta · 2 panels · Nhận xét ═══ */
            <Box sx={{ p: { xs: 1.5, sm: 2 }, maxHeight: 'calc(95dvh - 120px)', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Row 1: Meta info full width (Cuộc thi + Đội thi) */}
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                      Cuộc thi
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.25 }}>
                      {cases.find((c) => c.id === scoringForm.case_id)?.title || '— Cuộc thi chưa rõ —'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                      Đội thi
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.25 }}>
                      {(() => {
                        const team = teams.find((t) => t.id === scoringForm.team_id)
                        return team ? `${team.name}${team.university ? ` — ${team.university}` : ''}` : (isLoadingTeams ? 'Đang tải...' : scoringForm.team_id)
                      })()}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Row 2: 2 panels — Left: Bản phụ lục · Right: Đối chiếu */}
              <Grid container spacing={2} alignItems="stretch">
                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Header card */}
                    <Box
                      sx={{
                        px: 2,
                        py: 1.5,
                        background: gradientBrand,
                        color: 'common.white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachFile fontSize="small" />
                        <Typography variant="subtitle1" fontWeight={700}>
                          Bản phụ lục Chứng cứ
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={`${teamEvidenceList.length} file`}
                        sx={{ bgcolor: 'common.white', color: 'primary.main', fontWeight: 700 }}
                      />
                    </Box>
                    {/* Body */}
                    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                      {teamEvidenceList.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <AttachFile sx={{ fontSize: 48, color: 'primary.main', mb: 1.5 }} />
                          <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                            Đội chưa nộp chứng cứ nào
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.primary' }}>
                            Đội cần chọn chứng cứ từ pool đề thi để chấm điểm.
                          </Typography>
                        </Box>
                      ) : (
                        <>
                          <Typography variant="body2" sx={{ mb: 1, display: 'block', color: 'text.primary', fontWeight: 500 }}>
                            Danh sách chứng cứ đội đã chọn từ pool đề thi.
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, maxHeight: 460, overflow: 'auto', pr: 0.5 }}>
                            {teamEvidenceList.map((ev: any, idx: number) => {
                              const status = getEvidenceMatchStatus(ev)
                              const statusStyle =
                                status === 'correct'
                                  ? {
                                      borderColor: 'success.main',
                                      borderWidth: 2,
                                      bgcolor: 'rgba(46,125,50,0.06)',
                                      badge: { bg: 'success.main', label: '✓ Chuẩn' },
                                    }
                                  : status === 'decoy'
                                    ? {
                                        borderColor: 'warning.main',
                                        borderWidth: 2,
                                        bgcolor: 'rgba(237,108,2,0.08)',
                                        badge: { bg: 'warning.main', label: '✗ Nhiễu' },
                                      }
                                    : status === 'unlabeled'
                                      ? {
                                          borderColor: 'grey.400',
                                          borderWidth: 1,
                                          bgcolor: 'background.paper',
                                          badge: { bg: 'grey.500', label: '— Khác' },
                                        }
                                      : {
                                          borderColor: 'divider',
                                          borderWidth: 1,
                                          bgcolor: 'background.paper',
                                          badge: null,
                                        }
                              return (
                                <Box
                                  key={ev.id}
                                  sx={{
                                    px: 1.5,
                                    py: 1,
                                    border: '1px solid',
                                    borderColor: statusStyle.borderColor,
                                    borderWidth: statusStyle.borderWidth,
                                    borderRadius: 1.5,
                                    display: 'flex',
                                    gap: 1.5,
                                    alignItems: 'flex-start',
                                    bgcolor: statusStyle.bgcolor,
                                    transition: 'all 0.15s',
                                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      minWidth: 28,
                                      height: 28,
                                      borderRadius: '50%',
                                      bgcolor:
                                        status === 'correct'
                                          ? 'success.main'
                                          : status === 'decoy'
                                            ? 'warning.main'
                                            : 'primary.main',
                                      color: 'common.white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.78rem',
                                      fontWeight: 700,
                                      mt: 0.25,
                                    }}
                                  >
                                    {idx + 1}
                                  </Box>
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography variant="body2" fontWeight={600} noWrap title={ev.title}>
                                      {ev.title}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, flexWrap: 'wrap' }}>
                                      <Typography variant="caption" color="text.secondary" noWrap>
                                        {ev.file_name}
                                      </Typography>
                                      {ev.file_size && (
                                        <Typography variant="caption" color="text.secondary">
                                          · {(ev.file_size / 1024 / 1024).toFixed(2)} MB
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                  {statusStyle.badge && (
                                    <Chip
                                      size="small"
                                      label={statusStyle.badge.label}
                                      sx={{
                                        height: 22,
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        flexShrink: 0,
                                        bgcolor: statusStyle.badge.bg,
                                        color: 'common.white',
                                      }}
                                    />
                                  )}
                                  {!statusStyle.badge && ev.category && (
                                    <Chip
                                      label={ev.category}
                                      size="small"
                                      variant="outlined"
                                      sx={{ height: 22, fontSize: '0.7rem', flexShrink: 0 }}
                                    />
                                  )}
                                </Box>
                              )
                            })}
                          </Box>
                        </>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                  <Box sx={{ flex: 1, display: 'flex' }}>
                    {evidenceAuditPanel}
                  </Box>
                </Grid>
              </Grid>

              {/* Row 3: Điểm chốt phần Chứng cứ full width — AI auto-score
                  theo "chứng cứ đúng vs chứng cứ bẫy" (xem
                  compute_evidence_auto_score). KHÔNG admin nhập tay. */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  ✍️ Điểm chốt phần Chứng cứ
                </Typography>

                {aiEvidenceData ? (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      p: 1.75,
                      bgcolor: 'grey.100',
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'primary.main',
                    }}
                  >
                    <Chip
                      label={`Thu thập: ${aiEvidenceData.criterion_evidence_collection.score}/${aiEvidenceData.criterion_evidence_collection.max_score}đ`}
                      color="success"
                      sx={{ fontWeight: 700, fontSize: '0.85rem' }}
                    />
                    <Chip
                      label={`Sàng lọc: ${aiEvidenceData.criterion_evidence_filtering.score}/${aiEvidenceData.criterion_evidence_filtering.max_score}đ`}
                      color="warning"
                      sx={{ fontWeight: 700, fontSize: '0.85rem' }}
                    />
                    <Chip
                      label={`Tổng: ${aiEvidenceData.total_evidence_score}/50đ`}
                      color="primary"
                      sx={{ fontWeight: 700, fontSize: '0.85rem' }}
                    />
                    <Typography variant="body2" sx={{ ml: 'auto', fontStyle: 'italic', color: 'text.primary', fontWeight: 500 }}>
                      Hệ thống chấm tự động theo đối chiếu với đáp án BTC
                    </Typography>
                  </Box>
                ) : (
                  <Alert
                    severity="info"
                    variant="standard"
                    sx={{
                      bgcolor: '#E3F2FD',
                      border: '1px solid',
                      borderColor: 'info.main',
                      color: 'text.primary',
                      py: 1.25,
                      '& .MuiAlert-icon': { color: 'info.main' },
                    }}
                  >
                    Bấm <strong>"Tải báo cáo"</strong> ở panel Đối chiếu bên phải để hệ thống chấm tự động & đánh dấu chứng cứ đúng/sai trên Bản phụ lục.
                  </Alert>
                )}
              </Paper>
            </Box>
          ) : (
            /* ═══ Memorial Scoring Layout — 3 rows: Meta · 2 panels · Điểm chốt + Nhận xét ═══ */
            <Box sx={{ p: { xs: 1.5, sm: 2 }, maxHeight: 'calc(95dvh - 120px)', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Row 1: Meta info full width */}
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                      Cuộc thi
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.25 }}>
                      {cases.find((c) => c.id === scoringForm.case_id)?.title || '— Cuộc thi chưa rõ —'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                      Đội thi
                    </Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.25 }}>
                      {(() => {
                        const team = teams.find((t) => t.id === scoringForm.team_id)
                        return team ? `${team.name}${team.university ? ` — ${team.university}` : ''}` : (isLoadingTeams ? 'Đang tải...' : scoringForm.team_id)
                      })()}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>

              {/* Row 2: 2 panels — Memorial preview | Rubric scoring */}
              <Grid container spacing={2} alignItems="stretch">
                {/* LEFT — Hồ sơ Đơn khởi kiện (preview inline)
                    BUG4 (audit 2026-05-23): nếu đang chấm intro_video → render
                    player VIDEO của đội (không phải memorial PDF). Trước fix:
                    rubric video bên phải đúng, nhưng bên trái vẫn show memorial
                    PDF → GK chấm video mà không có video xem. */}
                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {scoringForm.submission_type === 'intro_video' ? (() => {
                      const t: any = teams.find((tt) => tt.id === scoringForm.team_id)
                      const iv = t?.intro_video || null
                      return (
                        <>
                          <Box sx={{ px: 2, py: 1.5, background: gradientBrand, color: 'common.white', display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Movie fontSize="small" />
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                              Video giới thiệu đội · {t?.name || 'Đội'}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 360, p: 2 }}>
                            {!iv ? (
                              <Alert severity="warning">
                                Đội chưa upload video giới thiệu. Không thể chấm phiếu Video — vui lòng nhắc đội nộp ở trang Video giới thiệu.
                              </Alert>
                            ) : isLoadingIntroVideo ? (
                              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6 }}>
                                <CircularProgress size={32} />
                                <Typography variant="body2" color="text.secondary">
                                  Đang tải video...
                                </Typography>
                              </Box>
                            ) : introVideoError ? (
                              <Alert severity="error">{introVideoError}</Alert>
                            ) : introVideoBlobUrl ? (
                              <video
                                controls
                                style={{ width: '100%', maxHeight: 480, background: '#000' }}
                                src={introVideoBlobUrl}
                              >
                                Trình duyệt không hỗ trợ video tag.
                              </video>
                            ) : (
                              <Alert severity="info">
                                Đã có file video <strong>{iv.file_name}</strong> nhưng chưa tải được. Vui lòng thử lại.
                              </Alert>
                            )}
                          </Box>
                        </>
                      )
                    })() : (() => {
                      const mem = memorials.find((m) => m.id === scoringForm.memorial_id)
                      return (
                        <>
                          <Box sx={{ px: 2, py: 1.5, background: gradientBrand, color: 'common.white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                              <Description fontSize="small" />
                              <Typography variant="subtitle1" fontWeight={700} noWrap>
                                Hồ sơ Đơn khởi kiện
                              </Typography>
                            </Box>
                            {mem && (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={async () => {
                                  try {
                                    const blob = await downloadMemorial(mem.id).unwrap()
                                    const url = window.URL.createObjectURL(blob)
                                    const a = document.createElement('a')
                                    a.href = url
                                    a.download = mem.file_name || 'memorial.pdf'
                                    document.body.appendChild(a)
                                    a.click()
                                    window.URL.revokeObjectURL(url)
                                    document.body.removeChild(a)
                                  } catch (e: any) {
                                    alert(e?.data?.detail || e?.message || 'Tải memorial thất bại')
                                  }
                                }}
                                sx={{
                                  bgcolor: 'common.white',
                                  color: 'primary.main',
                                  fontWeight: 700,
                                  textTransform: 'none',
                                  '&:hover': { bgcolor: 'grey.100' },
                                }}
                                startIcon={<Description fontSize="small" />}
                              >
                                Tải file
                              </Button>
                            )}
                          </Box>
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                            {!mem ? (
                              <Box sx={{ textAlign: 'center', py: 4, px: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                <Description sx={{ fontSize: 48, color: 'primary.main', mb: 1.5, alignSelf: 'center' }} />
                                <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
                                  Không tìm thấy Đơn khởi kiện
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                  Đội chưa nộp Đơn khởi kiện hoặc memorial đã bị xóa.
                                </Typography>
                              </Box>
                            ) : memorialPreviewLoading ? (
                              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 6 }}>
                                <CircularProgress size={32} />
                                <Typography variant="body2" color="text.secondary">
                                  Đang chuẩn bị bản xem trước...
                                </Typography>
                              </Box>
                            ) : memorialPreviewError ? (
                              <Box sx={{ p: 2 }}>
                                <Alert severity="warning" variant="standard" sx={{ mb: 2 }}>
                                  {memorialPreviewError}
                                </Alert>
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                  Tên file:
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.primary', wordBreak: 'break-all' }}>
                                  {mem.file_name}
                                </Typography>
                              </Box>
                            ) : memorialPreviewUrl ? (
                              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 400 }}>
                                <iframe
                                  src={memorialPreviewUrl}
                                  title={mem.file_name || 'Đơn khởi kiện'}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    minHeight: 480,
                                    border: 'none',
                                    flex: 1,
                                  }}
                                />
                              </Box>
                            ) : (
                              <Box sx={{ p: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Không có nội dung xem trước.
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </>
                      )
                    })()}
                  </Paper>
                </Grid>

                {/* RIGHT — Đánh giá theo Rubric */}
                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ px: 2, py: 1.5, background: gradientBrand, color: 'common.white', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Assessment fontSize="small" />
                        <Typography variant="subtitle1" fontWeight={700}>
                          Đánh giá theo Rubric
                        </Typography>
                      </Box>
                      {canUseAiAssistive && scoringForm.memorial_id && !isHearingBuiltinRubric && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={isLoadingAI ? <CircularProgress size={14} /> : <AutoAwesome />}
                          onClick={handleGetAISuggestions}
                          disabled={isLoadingAI || scoreFormLocked}
                          sx={{
                            bgcolor: 'common.white',
                            color: 'primary.main',
                            fontWeight: 700,
                            textTransform: 'none',
                            '&:hover': { bgcolor: 'grey.100' },
                          }}
                        >
                          {aiSuggestions ? 'Chạy lại AI' : 'Chạy AI gợi ý'}
                        </Button>
                      )}
                    </Box>
                    <Box sx={{ p: 2, flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Rubric Selection — hiển thị TẤT CẢ rubric (có badge phân biệt) */}
                      {rubrics.length > 0 ? (
                        <FormControl fullWidth size="small">
                          <InputLabel id="memorial-rubric-label">Rubric chấm điểm</InputLabel>
                          <Select
                            labelId="memorial-rubric-label"
                            value={scoringForm.rubric_id}
                            label="Rubric chấm điểm"
                            onChange={(e) => {
                              const rubricId = e.target.value
                              setScoringForm({
                                ...scoringForm,
                                rubric_id: rubricId,
                                rubric_scores: {},
                              })
                            }}
                          >
                            {rubrics.map((r) => {
                              const matchCase = r.case_id && r.case_id === scoringForm.case_id
                              const general = !r.case_id
                              const otherCase = r.case_id && r.case_id !== scoringForm.case_id
                              return (
                                <MenuItem key={r.id} value={r.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <Box sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {r.name}
                                    </Box>
                                    {matchCase && <Chip label="Cuộc thi này" size="small" color="success" sx={{ fontWeight: 600 }} />}
                                    {general && <Chip label="Chung" size="small" color="primary" sx={{ fontWeight: 600 }} />}
                                    {otherCase && <Chip label="Cuộc thi khác" size="small" color="warning" variant="outlined" />}
                                  </Box>
                                </MenuItem>
                              )
                            })}
                          </Select>
                        </FormControl>
                      ) : (
                        <Alert severity="warning" variant="filled">
                          Chưa có rubric nào trong hệ thống. Vui lòng tạo rubric trước khi chấm điểm.
                        </Alert>
                      )}

                      {/* C2 (audit 2026-05-22): bỏ selector "Hồ sơ" + "Vai trò
                          đại diện" trong dialog — flow mới luôn đi theo
                          submission_index pre-set từ row Chấm điểm panel
                          (R1DualTeamScoringPanel). Selector trong dialog gây
                          confusion + duplicate state với row. Pre-set tự động
                          khi handleOpenScoringDialog chọn submission_type +
                          submission_index. Hiển thị badge readonly để user biết
                          đang chấm hồ sơ nào. */}
                      {(() => {
                        const cur: any = cases.find((c) => c.id === scoringForm.case_id)
                        const isDualR1 =
                          cur?.r1_flow_version === 'dual_claimant_packets' &&
                          scoringForm.submission_type === 'memorial' &&
                          scoringForm.submission_index
                        if (!isDualR1) return null
                        const idx = scoringForm.submission_index
                        return (
                          <Chip
                            label={`Đang chấm: Hồ sơ ${idx}`}
                            sx={{
                              bgcolor: idx === 1 ? '#fce4ec' : '#e3f2fd',
                              color: idx === 1 ? '#c2185b' : '#1565c0',
                              fontWeight: 600,
                              alignSelf: 'flex-start',
                            }}
                          />
                        )
                      })()}

                      {/* AI suggestions analysis (only when loaded) */}
                      {aiSuggestions && (
                        <Alert
                          severity={
                            (aiSuggestions.confidence ?? 0) >= 0.7
                              ? 'success'
                              : (aiSuggestions.confidence ?? 0) >= 0.4
                                ? 'info'
                                : 'warning'
                          }
                          variant="standard"
                          sx={{
                            border: '1px solid',
                            borderColor:
                              (aiSuggestions.confidence ?? 0) >= 0.7
                                ? 'success.main'
                                : (aiSuggestions.confidence ?? 0) >= 0.4
                                  ? 'info.main'
                                  : 'warning.main',
                          }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              🤖 Phân tích AI sơ bộ
                            </Typography>
                            {aiSuggestions.suggested_scores &&
                              Object.keys(aiSuggestions.suggested_scores).length > 0 && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  color="success"
                                  onClick={applyAllAISuggestions}
                                  disabled={scoreFormLocked}
                                  sx={{ textTransform: 'none', fontWeight: 700 }}
                                >
                                  Áp dụng tất cả
                                </Button>
                              )}
                          </Box>
                          <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                            {aiSuggestions.analysis}
                          </Typography>
                          {aiSuggestions.recommendations && aiSuggestions.recommendations.length > 0 && (
                            <Box sx={{ mt: 1.5 }}>
                              <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Đề xuất:
                              </Typography>
                              <Box component="ul" sx={{ m: 0, pl: 3, mt: 0.5 }}>
                                {aiSuggestions.recommendations.map((rec: string, idx: number) => (
                                  <li key={idx}>
                                    <Typography variant="body2" sx={{ mb: 0.25 }}>{rec}</Typography>
                                  </li>
                                ))}
                              </Box>
                            </Box>
                          )}
                          {aiSuggestions.confidence != null && (
                            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="caption" fontWeight={600}>Độ tin cậy:</Typography>
                              <Box sx={{ flex: 1, height: 6, borderRadius: 1, background: '#E0E0E0', overflow: 'hidden', maxWidth: 180 }}>
                                <Box
                                  sx={{
                                    width: `${(aiSuggestions.confidence * 100).toFixed(0)}%`,
                                    height: '100%',
                                    background:
                                      aiSuggestions.confidence >= 0.7
                                        ? '#2E7D32'
                                        : aiSuggestions.confidence >= 0.4
                                          ? '#1565C0'
                                          : '#EF6C00',
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" fontWeight={700}>
                                {(aiSuggestions.confidence * 100).toFixed(0)}%
                              </Typography>
                            </Box>
                          )}
                        </Alert>
                      )}

                      {/* Rubric Scores list */}
                      {rubricCriteria.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                          {rubricCriteria.map((criterion) => {
                            const aiVal = aiSuggestions?.suggested_scores?.[criterion.key]
                            const judgeVal = scoringForm.rubric_scores[criterion.key]
                            const diverged =
                              aiVal !== undefined &&
                              judgeVal !== undefined &&
                              Math.abs(judgeVal - aiVal) >= 1.0
                            return (
                              <Box
                                key={criterion.key}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  gap: 2,
                                  p: 1.5,
                                  bgcolor: 'background.paper',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1.5,
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  transition: 'border-color 0.15s',
                                  '&:hover': { borderColor: 'primary.main' },
                                }}
                              >
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="body2" fontWeight={700} sx={{ color: 'text.primary' }}>
                                    {criterion.name}
                                  </Typography>
                                  {criterion.description && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      display="block"
                                      sx={{ mt: 0.5, lineHeight: 1.5 }}
                                    >
                                      {criterion.description}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap', mt: 0.75 }}>
                                    {criterion.weight && criterion.weight !== 1.0 && (
                                      <Chip label={`Weight: ${criterion.weight}`} size="small" />
                                    )}
                                    {aiVal !== undefined && (
                                      <Tooltip
                                        title={
                                          aiSuggestions?.per_criterion_reasoning?.[criterion.key]
                                            ? `🤖 ${aiSuggestions.per_criterion_reasoning[criterion.key]}`
                                            : 'Click để áp dụng điểm AI gợi ý.'
                                        }
                                        placement="top"
                                        arrow
                                      >
                                        <Chip
                                          label={`🤖 AI: ${aiVal.toFixed(1)}`}
                                          size="small"
                                          color="info"
                                          variant="outlined"
                                          onClick={
                                            scoreFormLocked
                                              ? undefined
                                              : () => applyAISuggestionForCriterion(criterion.key)
                                          }
                                          sx={{ cursor: scoreFormLocked ? 'default' : 'pointer', fontWeight: 600 }}
                                        />
                                      </Tooltip>
                                    )}
                                    {diverged && (
                                      <Chip
                                        label={`Lệch ${(judgeVal - aiVal).toFixed(1)}`}
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ fontWeight: 600 }}
                                      />
                                    )}
                                  </Box>
                                </Box>
                                <Box sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  flexShrink: 0,
                                  alignSelf: { xs: 'flex-end', sm: 'flex-start' },
                                  mt: { xs: 0.5, sm: 0 },
                                }}>
                                  <TextField
                                    type="number"
                                    value={scoringForm.rubric_scores[criterion.key] ?? ''}
                                    onChange={(e) => {
                                      const value = parseFloat(e.target.value) || 0
                                      const clamped = Math.max(0, Math.min(value, criterion.max_score))
                                      setScoringForm({
                                        ...scoringForm,
                                        rubric_scores: {
                                          ...scoringForm.rubric_scores,
                                          [criterion.key]: clamped,
                                        },
                                      })
                                    }}
                                    disabled={scoreFormLocked}
                                    inputProps={{
                                      min: 0,
                                      max: criterion.max_score,
                                      step: 0.5,
                                      style: { textAlign: 'right', padding: '6px 8px', fontWeight: 700 },
                                    }}
                                    size="small"
                                    sx={{ width: 84 }}
                                    placeholder="0"
                                  />
                                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    / {criterion.max_score}
                                  </Typography>
                                </Box>
                              </Box>
                            )
                          })}
                        </Box>
                      ) : (
                        <Alert severity="warning" variant="filled">
                          Chưa có rubric nào. Vui lòng chọn rubric hoặc tạo rubric mới.
                        </Alert>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              {/* Row 3: Tổng điểm + Nhận xét full width */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  ✍️ Điểm chốt & Nhận xét của Giám khảo
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    p: 1.75,
                    bgcolor: 'grey.100',
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: 'primary.main',
                    mb: 2,
                  }}
                >
                  <Chip
                    label={`Tổng: ${calculateTotalScore().toFixed(2)} / ${getMaxScore().toFixed(1)}đ`}
                    color="primary"
                    sx={{ fontWeight: 700, fontSize: '0.95rem' }}
                  />
                  {editingScore && (
                    <Chip
                      label={`Điểm cũ: ${editingScore.total_score.toFixed(2)}`}
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                  <Typography variant="body2" sx={{ ml: 'auto', fontStyle: 'italic', color: 'text.primary', fontWeight: 500 }}>
                    Tổng cộng tự động từ các tiêu chí trong Rubric
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  label="Nhận xét"
                  value={scoringForm.comments}
                  onChange={(e) => setScoringForm({ ...scoringForm, comments: e.target.value })}
                  multiline
                  rows={4}
                  disabled={scoreFormLocked}
                  placeholder="Ghi chú đánh giá về Đơn khởi kiện (tùy chọn)..."
                  variant="outlined"
                />
              </Paper>
            </Box>
          )}

        </DialogContent>
        {/* AC-7 — Banner cảnh báo khi có tiêu chí vượt max */}
        {hasRubricOverflow && (
          <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
            {Object.values(rubricOverflowErrors).map((e, i) => (
              <div key={i}>
                <strong>{e.name}</strong>: nhập {e.value} điểm — không được vượt quá {e.max} điểm.
              </div>
            ))}
          </Alert>
        )}
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={handleCloseScoringDialog}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
            }}
          >
            Hủy
          </Button>
          <Button
            onClick={handleSubmitScore}
            variant="outlined"
            disabled={isCreating || isUpdating || isSubmitting || scoreFormLocked || hasRubricOverflow}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
              minWidth: 120,
            }}
          >
            {(isCreating || isUpdating) ? (
              <CircularProgress size={20} />
            ) : editingScore ? (
              'Lưu nháp'
            ) : (
              'Lưu nháp'
            )}
          </Button>
          <Button
            onClick={handleFinalizeScore}
            variant="contained"
            disabled={isCreating || isUpdating || isSubmitting || scoreFormLocked || hasRubricOverflow}
            color="success"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1,
              minWidth: 160,
            }}
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Hoàn tất & Gửi điểm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Rubric Dialog */}
      <Dialog open={rubricDialogOpen} onClose={() => setRubricDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Tạo Rubric mới</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Tên rubric *"
              value={newRubric.name}
              onChange={(e) => setNewRubric({ ...newRubric, name: e.target.value })}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel>Cuộc thi (tùy chọn)</InputLabel>
              <Select
                value={newRubric.case_id || ''}
                label="Cuộc thi (tùy chọn)"
                onChange={(e) => setNewRubric({ ...newRubric, case_id: e.target.value || undefined })}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>Tất cả cuộc thi (Rubric chung)</span>
                    <Chip label="Chung" size="small" color="primary" />
                  </Box>
                </MenuItem>
                {cases.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {formatCaseDisplay(c)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Rubric chung:</strong> Có thể sử dụng cho tất cả các cuộc thi
                <br />
                <strong>Rubric riêng:</strong> Chỉ áp dụng cho cuộc thi được chọn
              </Typography>
            </Alert>
            <Divider />
            <Typography variant="h6">Tiêu chí chấm điểm</Typography>
            {newRubric.criteria.map((criterion, index) => (
              <Paper key={index} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Tên tiêu chí *"
                    value={criterion.name}
                    onChange={(e) => {
                      const updated = [...newRubric.criteria]
                      updated[index] = { ...criterion, name: e.target.value }
                      setNewRubric({ ...newRubric, criteria: updated })
                    }}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Mô tả"
                    value={criterion.description || ''}
                    onChange={(e) => {
                      const updated = [...newRubric.criteria]
                      updated[index] = { ...criterion, description: e.target.value }
                      setNewRubric({ ...newRubric, criteria: updated })
                    }}
                    fullWidth
                    multiline
                    rows={2}
                  />
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                      label="Điểm tối đa *"
                      type="number"
                      value={criterion.max_score}
                      onChange={(e) => {
                        const updated = [...newRubric.criteria]
                        updated[index] = { ...criterion, max_score: parseFloat(e.target.value) || 0 }
                        setNewRubric({ ...newRubric, criteria: updated })
                      }}
                      inputProps={{ min: 0, step: 0.5 }}
                      required
                    />
                    <TextField
                      label="Trọng số (weight)"
                      type="number"
                      value={criterion.weight || 1}
                      onChange={(e) => {
                        const updated = [...newRubric.criteria]
                        updated[index] = { ...criterion, weight: parseFloat(e.target.value) || 1 }
                        setNewRubric({ ...newRubric, criteria: updated })
                      }}
                      inputProps={{ min: 0, step: 0.1 }}
                    />
                    <IconButton
                      color="error"
                      onClick={() => {
                        const updated = newRubric.criteria.filter((_, i) => i !== index)
                        setNewRubric({ ...newRubric, criteria: updated })
                      }}
                      disabled={newRubric.criteria.length === 1}
                    >
                      <Delete />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => {
                const newCriterion: RubricCriterion = {
                  key: `criterion_${newRubric.criteria.length + 1}`,
                  name: `Tiêu chí ${newRubric.criteria.length + 1}`,
                  max_score: 10,
                  weight: 1,
                }
                setNewRubric({
                  ...newRubric,
                  criteria: [...newRubric.criteria, newCriterion],
                })
              }}
            >
              Thêm tiêu chí
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRubricDialogOpen(false)}>Hủy</Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!newRubric.name || newRubric.criteria.length === 0) {
                setSnackbar({
                  open: true,
                  message: 'Vui lòng điền đầy đủ thông tin',
                  severity: 'error',
                })
                return
              }
              try {
                // Convert criteria to match backend format
                const rubricData: RubricCreate = {
                  name: newRubric.name,
                  case_id: newRubric.case_id || undefined,
                  criteria: newRubric.criteria.map((c) => ({
                    key: c.key,
                    name: c.name,
                    description: c.description,
                    max_score: c.max_score,
                    weight: c.weight,
                  })),
                }
                const created = await createRubric(rubricData).unwrap()
                setSnackbar({
                  open: true,
                  message: 'Tạo rubric thành công',
                  severity: 'success',
                })
                setScoringForm({ ...scoringForm, rubric_id: created.id || '' })
                setRubricDialogOpen(false)
                setNewRubric({
                  name: '',
                  case_id: '',
                  criteria: [{ key: 'criterion_1', name: 'Tiêu chí 1', max_score: 10, weight: 1 }],
                })
              } catch (error: any) {
                setSnackbar({
                  open: true,
                  message: error?.data?.detail || 'Lỗi khi tạo rubric',
                  severity: 'error',
                })
              }
            }}
            disabled={isCreatingRubric || !newRubric.name || newRubric.criteria.length === 0}
          >
            {isCreatingRubric ? 'Đang tạo...' : 'Tạo rubric'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Nhập thay (Admin nhập điểm thay GK) — M07 §3 */}
      <Dialog open={proxyDialog.open} onClose={closeProxyDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Nhập thay phiếu chấm điểm
          <Typography variant="caption" display="block" color="text.secondary">
            Admin/Thư ký nhập điểm thay giám khảo gặp sự cố — sẽ ghi audit log với cờ
            via_proxy=true.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const score = scores.find((s) => s.id === proxyDialog.scoreId)
            const rubric = score ? rubrics.find((r) => r.id === score.rubric_id) : null
            if (!score) {
              return <Alert severity="warning">Phiếu không tồn tại.</Alert>
            }
            // Audit 2026-05-21: KHÔNG fallback ra raw id.
            const teamName = teams.find((t) => t.id === score.team_id)?.name || '— Đội chưa rõ —'
            const judgeName =
              judgeUsers.find((u: any) => u.id === score.judge_id)?.full_name ||
              'Chưa xác định'
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Alert severity="info" variant="outlined">
                  <strong>Phiếu:</strong> {score.scorecard_id || score.id}{' '}
                  • <strong>GK gốc:</strong> {judgeName} • <strong>Đội:</strong> {teamName}
                  {' '}• <strong>Vòng:</strong>{' '}
                  {score.round_type === 'semifinal' ? 'Bán kết' : 'Chung kết'}
                </Alert>

                <FormControl fullWidth size="small">
                  <InputLabel>Lý do nhập thay</InputLabel>
                  <Select
                    value={proxyDialog.proxyReason}
                    label="Lý do nhập thay"
                    onChange={(e) =>
                      setProxyDialog((s) => ({ ...s, proxyReason: e.target.value as any }))
                    }
                  >
                    <MenuItem value="no_device">Giám khảo không có thiết bị</MenuItem>
                    <MenuItem value="tech_issue">Sự cố kỹ thuật / mất kết nối</MenuItem>
                    <MenuItem value="requested">Giám khảo yêu cầu hỗ trợ</MenuItem>
                    <MenuItem value="other">Lý do khác</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Giám khảo uỷ quyền</InputLabel>
                  <Select
                    value={proxyDialog.delegatingJudgeId}
                    label="Giám khảo uỷ quyền"
                    onChange={(e) =>
                      setProxyDialog((s) => ({ ...s, delegatingJudgeId: e.target.value }))
                    }
                  >
                    {judgeUsers.map((u: any) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.full_name || u.username}
                        {u.email ? ` (${u.email})` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Điểm theo Rubric ({rubric?.name || 'Chưa gắn rubric'})
                  </Typography>
                  {rubric && rubric.criteria ? (
                    rubric.criteria.map((c) => (
                      <Box
                        key={c.key}
                        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {c.name}{' '}
                          <Typography component="span" variant="caption" color="text.secondary">
                            (tối đa {c.max_score}đ)
                          </Typography>
                        </Typography>
                        <TextField
                          type="number"
                          size="small"
                          value={proxyDialog.rubricScores[c.key] ?? ''}
                          onChange={(e) => {
                            const v = e.target.value === '' ? 0 : parseFloat(e.target.value)
                            setProxyDialog((s) => ({
                              ...s,
                              rubricScores: { ...s.rubricScores, [c.key]: v },
                            }))
                          }}
                          inputProps={{
                            min: 0,
                            max: c.max_score,
                            step: 0.5,
                            style: { width: 70, textAlign: 'right' },
                          }}
                        />
                      </Box>
                    ))
                  ) : (
                    <Alert severity="warning">
                      Phiếu chưa gắn Rubric — vui lòng seed rubric cho vòng này trước.
                    </Alert>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      Tổng:{' '}
                      {Object.values(proxyDialog.rubricScores)
                        .reduce((acc, v) => acc + (v || 0), 0)
                        .toFixed(1)}
                      /100
                    </Typography>
                  </Box>
                </Box>

                <TextField
                  label="Ghi chú của giám khảo"
                  multiline
                  rows={3}
                  value={proxyDialog.comments}
                  onChange={(e) =>
                    setProxyDialog((s) => ({ ...s, comments: e.target.value }))
                  }
                  placeholder="Ghi căn cứ chấm điểm hoặc ghi chú do GK cung cấp (giấy chấm, ghi âm…)"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={proxyDialog.submit}
                      onChange={(e) =>
                        setProxyDialog((s) => ({ ...s, submit: e.target.checked }))
                      }
                    />
                  }
                  label="Chốt phiếu ngay sau khi nhập (khoá + snapshot SHA-256)"
                />
              </Box>
            )
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeProxyDialog} disabled={isProxySubmitting}>
            Huỷ
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleProxySubmit}
            disabled={isProxySubmitting || !proxyDialog.scoreId}
          >
            {isProxySubmitting ? 'Đang xử lý…' : proxyDialog.submit ? 'Nhập + Chốt phiếu' : 'Lưu nháp'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 600, boxShadow: 4, minWidth: 320 }}
          action={
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => setSnackbar({ ...snackbar, open: false })}
            >
              <Close fontSize="small" />
            </IconButton>
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* 2026-05-25: Dialog mở khoá phiếu Bán kết / Chung kết + audit log.
          - Mode 'unlock': mở khoá kỹ thuật (admin sửa lỗi hệ thống, KHÔNG
            notify GK). Reason optional cho R1, BẮT BUỘC cho R2/R3.
          - Mode 'reopen': yêu cầu chấm lại (notify GK + audit log với
            action=score.reopen). Reason BẮT BUỘC ở mọi vòng.
          Audit log do BE auto-ghi qua AuditLogService.log_action — FE chỉ
          cần render unlock_history sẵn có trên score record. */}
      <Dialog
        open={unlockDialog.open}
        onClose={() => {
          if (unlockDialog.submitting) return
          setUnlockDialog({
            open: false,
            score: null,
            mode: 'unlock',
            reason: '',
            submitting: false,
            errorMsg: '',
          })
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <LockOpen color="warning" />
            <span>
              Mở khoá phiếu chấm
              {unlockDialog.score?.round_type === 'final'
                ? ' — Vòng Chung kết'
                : unlockDialog.score?.round_type === 'semifinal'
                ? ' — Vòng Bán kết'
                : ''}
            </span>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {unlockDialog.score && (() => {
            const sc = unlockDialog.score
            const team = teams.find((t) => t.id === sc.team_id)
            const judge = judgeUsers.find((u: any) => u.id === sc.judge_id)
            const judgeName =
              (judge as any)?.full_name ||
              (judge as any)?.username ||
              (judge as any)?.email ||
              sc.judge_id?.slice(0, 8) ||
              '—'
            const history = (sc as any).unlock_history || []
            return (
              <Stack spacing={2}>
                <Alert severity="warning" icon={<Warning />}>
                  Mở khoá phiếu sẽ chuyển <code>status='submitted'</code> →
                  <code>'draft'</code>, GK có thể chỉnh sửa lại. Hệ thống tự
                  ghi <b>audit log</b> với người unlock + lý do + timestamp.
                  {(sc.round_type === 'semifinal' || sc.round_type === 'final') &&
                    ' Phiếu Bán kết/Chung kết bắt buộc nhập lý do.'}
                </Alert>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Phiếu chấm
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5 }}>
                    <Chip size="small" label={`Đội: ${team?.name || sc.team_id?.slice(0, 8) || '—'}`} />
                    <Chip size="small" variant="outlined" label={`GK: ${judgeName}`} />
                    <Chip
                      size="small"
                      color={sc.round_type === 'final' ? 'error' : sc.round_type === 'semifinal' ? 'info' : 'default'}
                      variant="outlined"
                      label={
                        sc.round_type === 'final'
                          ? 'Chung kết'
                          : sc.round_type === 'semifinal'
                          ? 'Bán kết'
                          : sc.round_type || 'Vòng khác'
                      }
                    />
                    <Chip
                      size="small"
                      color="success"
                      label={`Tổng: ${(sc.total_score ?? 0).toFixed(2)}`}
                    />
                  </Stack>
                </Box>

                <FormControl size="small">
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Mục đích mở khoá
                  </Typography>
                  <RadioGroup
                    value={unlockDialog.mode}
                    onChange={(e) =>
                      setUnlockDialog((d) => ({
                        ...d,
                        mode: e.target.value as 'unlock' | 'reopen',
                        errorMsg: '',
                      }))
                    }
                  >
                    <FormControlLabel
                      value="unlock"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Mở khoá kỹ thuật
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Admin sửa lỗi nhập liệu / hệ thống. KHÔNG gửi notify cho GK.
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="reopen"
                      control={<Radio size="small" />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            Yêu cầu chấm lại
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Gửi notify cho GK + audit log với action <code>score.reopen</code>.
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>

                <TextField
                  label={
                    unlockDialog.mode === 'reopen' ||
                    sc.round_type === 'semifinal' ||
                    sc.round_type === 'final'
                      ? 'Lý do (bắt buộc)'
                      : 'Lý do (không bắt buộc)'
                  }
                  value={unlockDialog.reason}
                  onChange={(e) =>
                    setUnlockDialog((d) => ({ ...d, reason: e.target.value, errorMsg: '' }))
                  }
                  multiline
                  minRows={2}
                  maxRows={6}
                  fullWidth
                  size="small"
                  placeholder="Ví dụ: GK nhập nhầm tiêu chí III (yêu cầu chấm lại theo biên bản phiên)."
                  error={!!unlockDialog.errorMsg && !unlockDialog.reason.trim()}
                  helperText={
                    unlockDialog.errorMsg || 'Sẽ được lưu vào unlock_history + audit log.'
                  }
                />

                {history.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Lịch sử unlock ({history.length})
                    </Typography>
                    <Box
                      sx={{
                        maxHeight: 180,
                        overflowY: 'auto',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 1,
                      }}
                    >
                      {history.map((h: any, idx: number) => (
                        <Box
                          key={idx}
                          sx={{
                            py: 0.75,
                            borderBottom:
                              idx < history.length - 1 ? '1px dashed' : 'none',
                            borderColor: 'divider',
                          }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              size="small"
                              label={h.action || 'unlock'}
                              color={h.action === 'reopen' ? 'warning' : 'default'}
                              sx={{ height: 18, fontSize: 10 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {h.at ? new Date(h.at).toLocaleString('vi-VN') : '—'}
                            </Typography>
                            <Typography variant="caption">
                              · by {h.by_role || ''} {h.by?.slice(0, 8) || '—'}
                            </Typography>
                          </Stack>
                          {h.reason && (
                            <Typography
                              variant="caption"
                              color="text.primary"
                              sx={{ display: 'block', pl: 1, fontStyle: 'italic' }}
                            >
                              "{h.reason}"
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                {unlockDialog.errorMsg && unlockDialog.reason.trim() && (
                  <Alert severity="error">{unlockDialog.errorMsg}</Alert>
                )}
              </Stack>
            )
          })()}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() =>
              setUnlockDialog({
                open: false,
                score: null,
                mode: 'unlock',
                reason: '',
                submitting: false,
                errorMsg: '',
              })
            }
            disabled={unlockDialog.submitting || isUnlocking || isReopening}
          >
            Huỷ
          </Button>
          <Button
            variant="contained"
            color={unlockDialog.mode === 'reopen' ? 'warning' : 'primary'}
            startIcon={<LockOpen />}
            onClick={handleConfirmUnlockDialog}
            disabled={unlockDialog.submitting || isUnlocking || isReopening}
          >
            {unlockDialog.submitting || isUnlocking || isReopening
              ? 'Đang xử lý...'
              : unlockDialog.mode === 'reopen'
              ? 'Yêu cầu chấm lại + gửi notify'
              : 'Mở khoá phiếu'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
